// frontend/src/services/index.js - ALL API SERVICES CONSOLIDATED IN ONE FILE



import { v4 as uuidv4 } from 'uuid';
import { formatCurrency as sharedFormatCurrency } from '../utils';
import toast from 'react-hot-toast';
import { goto } from '../utils/navigation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// =============================================
// ERROR CLASSES
// =============================================

// Custom error classes for better error handling
class ApiError extends Error {
  constructor(message, status, response, operation = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
    this.operation = operation;
    this.timestamp = new Date().toISOString();
  }
}

class NetworkError extends Error {
  constructor(message, operation = null) {
    super(message);
    this.name = 'NetworkError';
    this.operation = operation;
    this.timestamp = new Date().toISOString();
  }
}

class ValidationError extends Error {
  constructor(message, errors) {
    super(message);
    this.name = 'ValidationError';
    this.errors = errors;
  }
}

class AdminApiError extends ApiError {
  constructor(message, status, response, operation = null) {
    super(message, status, response, operation);
    this.name = 'AdminApiError';
  }
}

class AdminNetworkError extends NetworkError {
  constructor(message, operation = null) {
    super(message, operation);
    this.name = 'AdminNetworkError';
  }
}

// =============================================
// REQUEST INTERCEPTORS
// =============================================

// Request interceptor for consistent headers and auth
// AUDIT FIX (H-11 + cross-origin follow-up):
// When the frontend is hosted on a different origin to the backend (the
// dev setup: localhost:3000 → localhost:5000) the JS at :3000 can't read
// the CSRF cookie set by :5000 via document.cookie. We solve that by
// fetching the token from the backend's GET /api/csrf endpoint (which
// also sets the cookie, so the backend's double-submit check still works),
// caching it in memory, and falling back to document.cookie for the
// same-origin case.
let _csrfTokenCache = null;
let _csrfPrimePromise = null;

function _readCsrfCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)scr_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

async function primeCsrfToken() {
  if (_csrfTokenCache) return _csrfTokenCache;
  if (_csrfPrimePromise) return _csrfPrimePromise;
  const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
  _csrfPrimePromise = fetch(apiUrl + '/csrf', { credentials: 'include' })
    .then(r => r.ok ? r.json() : null)
    .then(j => {
      _csrfTokenCache = (j && j.csrfToken) || _readCsrfCookie() || null;
      return _csrfTokenCache;
    })
    .catch(() => { _csrfTokenCache = _readCsrfCookie() || null; return _csrfTokenCache; })
    .finally(() => { _csrfPrimePromise = null; });
  return _csrfPrimePromise;
}

function getCsrfToken() {
  // First try the in-memory cache (set by primeCsrfToken), then cookie.
  return _csrfTokenCache || _readCsrfCookie();
}

// Kick off the prime on module load — non-blocking. If it fails, fallback
// to cookie reads will still work for the same-origin case.
if (typeof window !== 'undefined') {
  setTimeout(() => { primeCsrfToken().catch(() => {}); }, 0);
}

// Export so callers (e.g. login flow) can ensure the token is primed
// before submitting state-changing requests.
if (typeof window !== 'undefined') {
  window.__cwPrimeCsrf = primeCsrfToken;
}

// Stable per-browser device id. The backend keys sessions on X-Device-Id;
// without it every authenticated request registers a brand-new session.
function getStoredDeviceId() {
  try {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  } catch {
    return null;
  }
}

function getDeviceHeaders() {
  const deviceId = getStoredDeviceId();
  return deviceId ? { 'X-Device-Id': deviceId } : {};
}

class RequestInterceptor {
  static getDefaultHeaders() {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...getDeviceHeaders(),
    };
  }

  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  static getCsrfHeaders() {
    const tok = getCsrfToken();
    return tok ? { 'X-CSRF-Token': tok } : {};
  }

  static getHeaders(includeAuth = true, customHeaders = {}) {
    return {
      ...this.getDefaultHeaders(),
      ...(includeAuth ? this.getAuthHeaders() : {}),
      ...this.getCsrfHeaders(),
      ...customHeaders,
    };
  }
}

// Admin-specific request interceptor
class AdminRequestInterceptor {
  static getDefaultHeaders() {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...getDeviceHeaders(),
      'X-Admin-Client': 'web',
      'X-Client-Version': process.env.REACT_APP_VERSION || '1.0.0',
    };
  }

  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  static getCsrfHeaders() {
    const tok = getCsrfToken();
    return tok ? { 'X-CSRF-Token': tok } : {};
  }

  static getHeaders(includeAuth = true, customHeaders = {}) {
    return {
      ...this.getDefaultHeaders(),
      ...(includeAuth ? this.getAuthHeaders() : {}),
      ...this.getCsrfHeaders(),
      ...customHeaders,
    };
  }
}

// =============================================
// RESPONSE INTERCEPTORS
// =============================================

// Response interceptor for consistent error handling
class ResponseInterceptor {
  static async handleResponse(response, operation = null) {
    // Handle network timeouts and other fetch-level errors
    if (!response) {
      throw new NetworkError('Network request failed - no response received', operation);
    }

    const contentType = response.headers.get('content-type');
    let data;

    try {
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else if (contentType && contentType.includes('text/')) {
        data = await response.text();
      } else {
        // Handle binary responses (images, files, etc.)
        data = await response.blob();
      }
    } catch (parseError) {
      throw new ApiError(
        'Failed to parse response',
        response.status,
        { parseError: parseError.message },
        operation
      );
    }

    // Handle different HTTP status codes
    if (!response.ok) {
      const errorMessage = data?.message || data?.error || `HTTP ${response.status}: ${response.statusText}`;
      
      // Handle specific status codes
      switch (response.status) {
        case 400:
          if (data?.errors) {
            throw new ValidationError(errorMessage, data.errors);
          }
          throw new ApiError(errorMessage, 400, data, operation);
        
        case 401:
          // Clear auth token on unauthorized
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          // Redirect to login (router-based, preserves toast + state). Stable
          // toastId prevents duplicates if multiple in-flight calls 401 at once.
          if (!window.location.pathname.includes('/login')) {
            toast.error('Your session has expired. Please log in again.', {
              toastId: 'session-expired',
            });
            const fromPath = window.location.pathname + window.location.search;
            goto('/login', {
              replace: true,
              state: { from: { pathname: fromPath }, reason: 'session_expired' },
            });
          }
          throw new ApiError('Session expired. Please log in again.', 401, data, operation);
        
        case 403:
          toast.error('Access denied. You do not have permission to perform this action.', {
            toastId: 'forbidden',
          });
          throw new ApiError('Access denied. You do not have permission to perform this action.', 403, data, operation);
        
        case 404:
          throw new ApiError('Resource not found.', 404, data, operation);
        
        case 409:
          throw new ApiError('Conflict: ' + errorMessage, 409, data, operation);
          
        case 422:
          throw new ValidationError(errorMessage, data?.errors || {});
        
        case 429:
          throw new ApiError('Too many requests. Please try again later.', 429, data, operation);
        
        case 500:
          throw new ApiError('Internal server error. Please try again later.', 500, data, operation);
          
        case 503:
          throw new ApiError('Service temporarily unavailable. Please try again later.', 503, data, operation);
        
        default:
          throw new ApiError(errorMessage, response.status, data, operation);
      }
    }

    return data;
  }
}

// Admin-specific response interceptor with detailed error handling
class AdminResponseInterceptor extends ResponseInterceptor {
  static async handleResponse(response, operation = null) {
    if (!response) {
      throw new AdminNetworkError('Network request failed - no response received', operation);
    }

    const contentType = response.headers.get('content-type');
    let data;

    try {
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else if (contentType && contentType.includes('text/')) {
        data = await response.text();
      } else {
        // Handle binary responses (images, files, etc.)
        data = await response.blob();
      }
    } catch (parseError) {
      throw new AdminApiError(
        'Failed to parse response',
        response.status,
        { parseError: parseError.message },
        operation
      );
    }

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || `HTTP ${response.status}: ${response.statusText}`;
      
      switch (response.status) {
        case 400:
          throw new AdminApiError(errorMessage, 400, data, operation);
        
        case 401:
          // Clear main auth tokens and redirect to login (router-based)
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          if (!window.location.pathname.includes('/login')) {
            toast.error('Your admin session has expired. Please log in again.', {
              toastId: 'admin-session-expired',
            });
            goto('/login', {
              replace: true,
              state: {
                from: { pathname: '/admin' },
                reason: 'session_expired',
              },
            });
          }
          throw new AdminApiError('Session expired. Please log in again.', 401, data, operation);
        
        case 403:
          toast.error('Access denied. Insufficient admin privileges.', {
            toastId: 'admin-forbidden',
          });
          throw new AdminApiError('Access denied. Insufficient admin privileges.', 403, data, operation);
        
        case 404:
          throw new AdminApiError('Resource not found.', 404, data, operation);
        
        case 409:
          throw new AdminApiError('Conflict: ' + errorMessage, 409, data, operation);
        
        case 422:
          throw new AdminApiError('Validation failed: ' + errorMessage, 422, data, operation);
        
        case 429:
          throw new AdminApiError('Too many requests. Please try again later.', 429, data, operation);
        
        case 500:
          throw new AdminApiError('Internal server error. Please try again later.', 500, data, operation);
        
        case 503:
          throw new AdminApiError('Service temporarily unavailable. Please try again later.', 503, data, operation);
        
        default:
          throw new AdminApiError(errorMessage, response.status, data, operation);
      }
    }

    return data;
  }
}

// =============================================
// REQUEST CONFIGURATION
// =============================================

// Utility class for request configuration
class RequestConfig {
  static createFetchOptions(method = 'GET', body = null, headers = {}, signal = null, options = {}) {
    const fetchOptions = {
      method,
      headers: RequestInterceptor.getHeaders(true, headers),
      signal,
      credentials: options.credentials || 'same-origin',
      mode: options.mode || 'cors',
      ...options,
    };

    if (body && method !== 'GET' && method !== 'HEAD') {
      if (body instanceof FormData) {
        // Remove Content-Type for FormData - browser will set it with boundary
        delete fetchOptions.headers['Content-Type'];
        fetchOptions.body = body;
      } else if (body instanceof Blob) {
        fetchOptions.body = body;
      } else {
        fetchOptions.body = JSON.stringify(body);
      }
    }

    return fetchOptions;
  }

  static createAbortController(timeoutMs = 30000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    return {
      controller,
      cleanup: () => clearTimeout(timeoutId),
    };
  }
}

// Admin request configuration with enhanced options
class AdminRequestConfig extends RequestConfig {
  static createFetchOptions(method = 'GET', body = null, headers = {}, signal = null, options = {}) {
    const fetchOptions = {
      method,
      headers: AdminRequestInterceptor.getHeaders(true, headers),
      signal,
      credentials: 'include', // Include cookies for admin sessions
      mode: 'cors',
      ...options,
    };

    if (body && method !== 'GET' && method !== 'HEAD') {
      if (body instanceof FormData) {
        // Remove Content-Type for FormData
        delete fetchOptions.headers['Content-Type'];
        fetchOptions.body = body;
      } else if (body instanceof Blob) {
        fetchOptions.body = body;
      } else {
        fetchOptions.body = JSON.stringify(body);
      }
    }

    return fetchOptions;
  }

  static createAbortController(timeoutMs = 45000) { // Longer timeout for admin operations
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);
    
    return {
      controller,
      cleanup: () => clearTimeout(timeoutId),
    };
  }
}

// =============================================
// REQUEST MANAGERS
// =============================================

// Rate limiting and retry logic
class RequestManager {
  static rateLimitMap = new Map();
  static retryDelays = [1000, 2000, 4000]; // Exponential backoff

  static checkRateLimit(endpoint) {
    const now = Date.now();
    const rateLimitInfo = this.rateLimitMap.get(endpoint);
    
    if (rateLimitInfo && now < rateLimitInfo.resetTime) {
      throw new ApiError(
        `Rate limit exceeded for ${endpoint}. Try again in ${Math.ceil((rateLimitInfo.resetTime - now) / 1000)} seconds.`,
        429
      );
    }
  }

  static updateRateLimit(endpoint, response) {
    const resetTime = response.headers.get('X-RateLimit-Reset');
    if (resetTime) {
      this.rateLimitMap.set(endpoint, {
        resetTime: parseInt(resetTime) * 1000, // Convert to milliseconds
      });
    }
  }

  static async retryRequest(requestFn, maxRetries = 3) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on certain errors
        if (
          error instanceof ValidationError ||
          (error instanceof ApiError && [400, 401, 403, 404].includes(error.status))
        ) {
          throw error;
        }
        
        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Wait before retrying
        const delay = this.retryDelays[attempt] || this.retryDelays[this.retryDelays.length - 1];
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }
}

// Enhanced admin request manager with audit logging
class AdminRequestManager extends RequestManager {
  static auditLog = [];
  static retryDelays = [1000, 3000, 6000, 12000]; // More aggressive retry for admin operations

  static logOperation(operation, method, endpoint, success, error = null) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      operation,
      method,
      endpoint,
      success,
      error: error ? error.message : null,
      adminUser: this.getCurrentAdminUser(),
    };
    
    this.auditLog.push(logEntry);
    
    // Keep only last 1000 entries
    if (this.auditLog.length > 1000) {
      this.auditLog.shift();
    }
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Admin Operation:', logEntry);
    }
  }

  static getCurrentAdminUser() {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email || payload.id || 'unknown';
      }
    } catch (error) {
      // Ignore token parsing errors
    }
    return 'anonymous';
  }

  static getAuditLog() {
    return [...this.auditLog];
  }

  static clearAuditLog() {
    this.auditLog = [];
  }

  static async retryRequest(requestFn, operation, maxRetries = 3) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await requestFn();
        this.logOperation(operation, 'unknown', 'unknown', true);
        return result;
      } catch (error) {
        lastError = error;
        
        // Don't retry on certain errors
        if (
          error instanceof AdminApiError && 
          [400, 401, 403, 404, 409, 422].includes(error.status)
        ) {
          this.logOperation(operation, 'unknown', 'unknown', false, error);
          throw error;
        }
        
        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          this.logOperation(operation, 'unknown', 'unknown', false, error);
          throw error;
        }
        
        // Wait before retrying
        const delay = this.retryDelays[attempt] || this.retryDelays[this.retryDelays.length - 1];
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }
}

// =============================================
// MAIN API SERVICE CLASS
// =============================================

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // =====================
  // CORE HTTP METHODS
  // =====================

  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      timeout = 30000,
      cache = false,
      retries = 3,
      skipAuthRetry = false, // New option to skip auth retry for certain endpoints
    } = options;

    const url = `${this.baseURL}${endpoint}`;
    // AUDIT FIX P-9: only build a cache key for GETs; non-GET bodies
    // would otherwise live in the in-memory cache map indefinitely.
    const cacheKey = method === 'GET' ? `GET:${url}` : null;

    // Check cache for GET requests
    if (method === 'GET' && cache) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;
    }

    // Check rate limiting
    RequestManager.checkRateLimit(endpoint);

    const { controller, cleanup } = RequestConfig.createAbortController(timeout);

    try {
      const requestFn = async (isRetry = false) => {
        try {
          const fetchOptions = RequestConfig.createFetchOptions(method, body, headers, controller.signal);
          let response = await fetch(url, fetchOptions);
          
          // Handle 403 CSRF rejection — re-prime then retry once.
          if (response.status === 403 && !isRetry && method !== 'GET') {
            try {
              _csrfTokenCache = null;
              await primeCsrfToken();
              const retryFetchOptions2 = RequestConfig.createFetchOptions(method, body, headers, controller.signal);
              const retried = await fetch(url, retryFetchOptions2);
              if (retried.status !== 403) {
                response = retried;
              }
            } catch (_) { /* fall through */ }
          }
          // Handle 401 Unauthorized (token refresh flow)
          if (response.status === 401 && !skipAuthRetry && !isRetry) {
            try {
              // Attempt to refresh token
              await this.refreshToken();
              
              // Retry with new token
              const retryFetchOptions = RequestConfig.createFetchOptions(method, body, headers, controller.signal);
              response = await fetch(url, retryFetchOptions);
            } catch (refreshError) {
              // If refresh fails, clear tokens and throw
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              throw new ApiError('Session expired. Please log in again.', 401);
            }
          }

          // Update rate limit info
          RequestManager.updateRateLimit(endpoint, response);
          
          const data = await ResponseInterceptor.handleResponse(response);
          
          // Cache successful GET requests
          if (method === 'GET' && cache && data) {
            this.setCache(cacheKey, data);
          }
          
          return data;
        } catch (error) {
          // Convert fetch errors to our custom errors
          if (error.name === 'AbortError') {
            throw new NetworkError('Request timeout');
          }
          if (error.name === 'TypeError' && error.message.includes('fetch')) {
            throw new NetworkError('Network error - please check your connection');
          }
          throw error;
        }
      };

      return await RequestManager.retryRequest(() => requestFn(), retries);
    } finally {
      cleanup();
    }
  }

  // Enhanced refresh token method
  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new ApiError('No refresh token available', 401);
    }

    try {
      // Use skipAuthRetry to prevent infinite loops
      const data = await this.post(
        '/auth/refresh',
        { refreshToken },
        { skipAuthRetry: true }
      );

      if (data.accessToken) {
        localStorage.setItem('authToken', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        return data;
      }
      throw new ApiError('Failed to refresh token', 401);
    } catch (error) {
      // Clear tokens if refresh fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  }

  // Cache management
  getFromCache(key) {
    const item = this.cache.get(key);
    if (item && Date.now() - item.timestamp < this.cacheTimeout) {
      return item.data;
    }
    this.cache.delete(key);
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  clearCache(pattern = null) {
    if (pattern) {
      const regex = new RegExp(pattern);
      for (const key of this.cache.keys()) {
        if (regex.test(key)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  // Convenience methods with added skipAuthRetry option
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  async put(endpoint, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  async patch(endpoint, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // =====================
  // AUTHENTICATION APIs
  // =====================

  async login(credentials) {
    try {
      // Ensure credentials is properly formatted
      const payload = {
        email: credentials.email,
        password: credentials.password,
        deviceId: uuidv4(), // Generate new device ID if needed
        deviceFingerprint: navigator.userAgent
      };

      const data = await this.post('/auth/login', payload);
      
      if (data.success) {
        if (data.accessToken) {
          localStorage.setItem('authToken', data.accessToken);
        }
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        this.clearCache();
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async register(userData) {
    try {
      const data = await this.post('/auth/register', userData);
      
      if (data.success && data.accessToken) {
        localStorage.setItem('authToken', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
      }
      
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }


  async verifyToken() {
    try {
      return await this.get('/auth/verify');
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        // Try to refresh token
        try {
          await this.refreshToken();
          return await this.get('/auth/verify');
        } catch (refreshError) {
          throw refreshError;
        }
      }
      throw error;
    }
  }

  async resendVerification(email) {
    try {
      const response = await this.post('/auth/resend-verification', { email });

      if (response.success) {
        // Successfully resent verification email
        return response;
      } else if (response.error?.code === 400 && response.error?.message === 'Email already verified') {
        // Email is already verified, refresh user data to sync status
        await this.updateUserProfile({}); // Empty update to fetch latest user data
        return { success: false, message: 'Email already verified', emailVerified: true };
      }
      throw new ApiError(response.error?.message || 'Failed to resend verification email', response.status);
    } catch (error) {
      console.error('Error resending verification:', error);
      throw error instanceof ApiError ? error : new ApiError('Failed to resend verification email', 500);
    }
  }

  // =====================
  // USER PROFILE APIs
  // =====================

  async updateUserProfile(updates) {
    try {
      const data = await this.put('/auth/profile', updates);
      this.clearCache('/auth');
      return data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  async uploadProfilePicture(imageFile) {
    try {
      const formData = new FormData();
      formData.append('profilePicture', imageFile);
      
      const data = await this.post('/auth/profile/picture', formData);
      this.clearCache('/auth');
      return data;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  }

  async changePassword(passwordData) {
    return this.post('/auth/change-password', passwordData);
  }

  async changeEmail(emailData) {
    return this.post('/auth/change-email', emailData);
  }

  async confirmEmailChange(token) {
    return this.post('/auth/confirm-email-change', { token });
  }

  // =====================
  // MFA (Multi-Factor Authentication) APIs
  // =====================

  async enableMFA(mfaData) {
    return this.post('/auth/mfa/enable', mfaData);
  }

  async verifyMFASetup(code) {
    return this.post('/auth/mfa/verify-setup', { code });
  }

  async disableMFA(password) {
    return this.post('/auth/mfa/disable', { password });
  }

  async verifyMFA(userId, code, deviceId = null, trustDevice = false) {
    return this.post('/auth/verify-mfa', {
      userId,
      code,
      deviceId,
      trustDevice,
    });
  }

  // =====================
  // SESSION MANAGEMENT APIs
  // =====================

// Updated session management methods for your frontend API service
// Replace the existing session methods in your frontend/src/services/index.js

// =============================================
// REAL SESSION MANAGEMENT APIs (No Mock Data)
// =============================================

/**
 * Get user's active sessions with real data from backend
 * @param {object} options - Request options
 * @returns {Promise<object>} Real sessions data
 */
async getActiveSessions(options = {}) {
  try {
    const data = await this.get('/auth/sessions', {
      operation: 'Get Active Sessions',
      cache: false, // Always fetch fresh session data
      headers: {
        'X-Device-Id': this.generateDeviceId(),
        ...options.headers
      },
      ...options
    });
    
    if (!data.success) {
      throw new ApiError(data.error?.message || 'Failed to fetch sessions', data.error?.code || 500);
    }
    
    return {
      sessions: data.sessions || [],
      count: data.count || 0,
      currentSessionId: data.currentSessionId,
      success: true
    };
  } catch (error) {
    console.error('Error fetching active sessions:', error);
    if (error instanceof ApiError && error.status === 401) {
      return { sessions: [], count: 0, success: false, needsAuth: true };
    }
    throw error;
  }
}

/**
 * Get session statistics (real data)
 * @param {object} options - Request options
 * @returns {Promise<object>} Session statistics
 */
async getSessionStats(options = {}) {
  try {
    const data = await this.get('/auth/sessions/stats', {
      operation: 'Get Session Statistics',
      cache: false,
      ...options
    });
    
    return {
      stats: data.data || {},
      success: true
    };
  } catch (error) {
    console.error('Error fetching session statistics:', error);
    throw error;
  }
}

/**
 * Get detailed session information
 * @param {string} sessionId - Session ID
 * @param {object} options - Request options
 * @returns {Promise<object>} Session details
 */
async getSessionDetails(sessionId, options = {}) {
  try {
    if (!sessionId) {
      throw new ValidationError('Session ID is required');
    }
    
    const data = await this.get(`/auth/sessions/${sessionId}`, {
      operation: 'Get Session Details',
      cache: false,
      ...options
    });
    
    return {
      session: data.data,
      success: true
    };
  } catch (error) {
    console.error('Error fetching session details:', error);
    throw error;
  }
}

/**
 * Get real location history from actual sessions
 * @param {object} filters - Filter options
 * @param {object} options - Request options
 * @returns {Promise<object>} Real location history data
 */
async getLocationHistory(filters = {}, options = {}) {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters
    if (filters.days) params.append('days', filters.days.toString());
    if (filters.includeRisk) params.append('includeRisk', 'true');
    if (filters.country) params.append('country', filters.country);
    
    const queryString = params.toString();
    const endpoint = `/auth/location-history${queryString ? `?${queryString}` : ''}`;
    
    const data = await this.get(endpoint, {
      operation: 'Get Location History',
      cache: false, // Location data should be fresh
      ...options
    });
    
    return {
      data: data.data || [],
      summary: data.summary || {},
      success: true
    };
  } catch (error) {
    console.error('Error fetching location history:', error);
    throw error;
  }
}

/**
 * Get current session security status (real data)
 * @param {object} options - Request options
 * @returns {Promise<object>} Real security status
 */
async getSessionSecurity(options = {}) {
  try {
    const data = await this.get('/auth/session-security', {
      operation: 'Get Session Security',
      cache: false, // Security status should always be real-time
      headers: {
        'X-Session-Id': this.generateSessionId(),
        'X-Device-Id': this.generateDeviceId(),
        ...options.headers
      },
      ...options
    });
    
    return {
      securityStatus: data.data,
      success: true
    };
  } catch (error) {
    console.error('Error fetching session security:', error);
    throw error;
  }
}

/**
 * Revoke a specific session (real implementation)
 * @param {string} sessionId - Session ID to revoke
 * @param {object} options - Request options
 * @returns {Promise<object>} Revocation result
 */
async revokeSession(sessionId, options = {}) {
  try {
    if (!sessionId) {
      throw new ValidationError('Session ID is required');
    }
    
    const data = await this.delete(`/auth/sessions/${sessionId}`, {
      operation: 'Revoke Session',
      ...options
    });
    
    return {
      sessionId,
      message: data.message,
      success: true
    };
  } catch (error) {
    console.error('Error revoking session:', error);
    throw error;
  }
}

/**
 * Revoke all sessions except current (real implementation)
 * @param {boolean} keepCurrent - Whether to keep current session active
 * @param {object} options - Request options
 * @returns {Promise<object>} Revocation result
 */
async revokeAllSessions(keepCurrent = true, options = {}) {
  try {
    const data = await this.post('/auth/sessions/revoke-all', {
      keepCurrent
    }, {
      operation: 'Revoke All Sessions',
      ...options
    });
    
    return {
      message: data.message,
      revokedCount: data.revokedCount,
      success: true
    };
  } catch (error) {
    console.error('Error revoking all sessions:', error);
    throw error;
  }
}



/**
 * Check verification status with real user data
 * @param {object} options - Request options
 * @returns {Promise<object>} Verification status
 */
async checkVerificationStatus(options = {}) {
  try {
    const data = await this.get('/auth/check-verification', {
      operation: 'Check Verification Status',
      cache: false,
      ...options
    });
    
    return {
      user: data.user,
      emailVerified: data.emailVerified,
      success: true
    };
  } catch (error) {
    console.error('Error checking verification status:', error);
    throw error;
  }
}

/**
 * Resend verification email (real implementation)
 * @param {string} email - Email address
 * @param {object} options - Request options
 * @returns {Promise<object>} Result
 */
async resendVerification(email, options = {}) {
  try {
    if (!email) {
      throw new ValidationError('Email is required');
    }
    
    const data = await this.post('/auth/resend-verification', { email }, {
      operation: 'Resend Verification',
      ...options
    });
    
    return {
      message: data.message,
      emailVerified: data.emailVerified,
      success: true
    };
  } catch (error) {
    console.error('Error resending verification:', error);
    throw error;
  }
}

// =============================================
// UTILITY METHODS FOR REAL SESSION MANAGEMENT
// =============================================

/**
 * Generate or get persistent device ID
 * @returns {string} Device ID
 */
generateDeviceId() {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('deviceId', deviceId);
  }
  return deviceId;
}

/**
 * Generate session ID for tracking
 * @returns {string} Session ID
 */
generateSessionId() {
  let sessionId = sessionStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = `frontend_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
}

/**
 * Get comprehensive device fingerprint
 * @returns {object} Device fingerprint
 */
getDeviceFingerprint() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: {
      width: screen.width,
      height: screen.height,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth
    },
    window: {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      outerWidth: window.outerWidth,
      outerHeight: window.outerHeight
    },
    connection: navigator.connection ? {
      effectiveType: navigator.connection.effectiveType,
      downlink: navigator.connection.downlink
    } : null,
    timestamp: new Date().toISOString()
  };
}

/**
 * Format session data for display with real data
 * @param {object} session - Real session object from backend
 * @returns {object} Formatted session data
 */
formatSessionForDisplay(session) {
  if (!session) return null;
  
  return {
    id: session.id,
    deviceInfo: {
      name: this.getDeviceDisplayName(session.deviceInfo),
      browser: session.deviceInfo?.browser || 'Unknown',
      os: session.deviceInfo?.os || 'Unknown',
      type: session.deviceInfo?.deviceType || 'Unknown',
      ip: session.deviceInfo?.ip
    },
    location: {
      formatted: session.location?.formatted || 'Unknown Location',
      city: session.location?.city || 'Unknown',
      country: session.location?.country || 'Unknown',
      countryCode: session.location?.countryCode || 'XX',
      flag: session.location?.countryFlag || '🏳️',
      riskLevel: session.location?.riskLevel || 'unknown',
      isProxy: session.location?.isProxy || false
    },
    security: {
      isCurrent: session.isCurrent || false,
      isVerified: session.isVerified || false,
      riskScore: session.location?.riskScore || 0
    },
    activity: {
      requestCount: session.requestCount || 0,
      lastEndpoint: session.lastEndpoint
    },
    timestamps: {
      created: session.createdAt,
      lastActive: session.lastActive,
      createdFormatted: this.formatRelativeTime(session.createdAt),
      lastActiveFormatted: this.formatRelativeTime(session.lastActive)
    }
  };
}

/**
 * Get display name for device
 * @param {object} deviceInfo - Device information
 * @returns {string} Display name
 */
getDeviceDisplayName(deviceInfo) {
  if (!deviceInfo) return 'Unknown Device';
  
  const browser = deviceInfo.browser || 'Unknown Browser';
  const os = deviceInfo.os || 'Unknown OS';
  const deviceType = deviceInfo.deviceType;
  
  if (deviceType === 'Mobile') {
    return `${browser} on ${os} (Mobile)`;
  } else if (deviceType === 'Tablet') {
    return `${browser} on ${os} (Tablet)`;
  } else {
    return `${browser} on ${os}`;
  }
}

/**
 * Check if current session meets security requirements
 * @returns {Promise<object>} Security check result
 */
async checkSessionSecurity() {
  try {
    const securityData = await this.getSessionSecurity();
    const { securityStatus } = securityData;
    
    if (!securityStatus) {
      return { secure: false, reason: 'No security data available' };
    }
    
    // Define security criteria
    const isSecure = 
      securityStatus.security.riskLevel === 'low' &&
      !securityStatus.security.requiresVerification &&
      securityStatus.security.riskScore < 30;
    
    return {
      secure: isSecure,
      riskLevel: securityStatus.security.riskLevel,
      riskScore: securityStatus.security.riskScore,
      recommendations: securityStatus.security.recommendations || [],
      location: securityStatus.location?.formatted
    };
  } catch (error) {
    console.error('Error checking session security:', error);
    return { secure: false, reason: 'Security check failed' };
  }
}

/**
 * Get security recommendations for current session
 * @returns {Promise<Array>} Array of security recommendations
 */
async getSecurityRecommendations() {
  try {
    const securityData = await this.getSessionSecurity();
    const recommendations = securityData.securityStatus?.security?.recommendations || [];
    
    return recommendations.map(rec => ({
      type: rec.type,
      message: rec.message,
      priority: rec.priority,
      actionable: true
    }));
  } catch (error) {
    console.error('Error getting security recommendations:', error);
    return [];
  }
}

/**
 * Enhanced login with real session tracking
 * @param {object} credentials - Login credentials
 * @returns {Promise<object>} Login result
 */
async login(credentials) {
  try {
    // Add device fingerprint to login request
    const deviceFingerprint = this.getDeviceFingerprint();
    const deviceId = this.generateDeviceId();
    
    const payload = {
      email: credentials.email,
      password: credentials.password,
      deviceId,
      deviceFingerprint,
      rememberMe: credentials.rememberMe || false
    };

    const data = await this.post('/auth/login', payload, {
      headers: {
        'X-Device-Id': deviceId
      }
    });
    
    if (data.success || data.accessToken) {
      if (data.accessToken) {
        localStorage.setItem('authToken', data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      this.clearCache();
    }
    
    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Monitor session activity in real-time
 * @param {function} callback - Callback for session updates
 * @returns {function} Cleanup function
 */
monitorSessionActivity(callback) {
  const interval = setInterval(async () => {
    try {
      const sessions = await this.getActiveSessions();
      callback(sessions);
    } catch (error) {
      console.error('Session monitoring error:', error);
    }
  }, 60000); // Check every minute
  
  return () => clearInterval(interval);
}

/**
 * Get location-based insights from real session data
 * @param {number} days - Number of days to analyze
 * @returns {Promise<object>} Location insights
 */
async getLocationInsights(days = 30) {
  try {
    const locationHistory = await this.getLocationHistory({ 
      days, 
      includeRisk: true 
    });
    
    const insights = {
      totalLocations: locationHistory.data.length,
      countries: [...new Set(locationHistory.data.map(l => l.countryCode))],
      mostFrequentLocation: null,
      riskySessions: locationHistory.data.filter(l => l.riskLevel === 'high').length,
      proxyUsage: locationHistory.data.filter(l => l.isProxy).length,
      travelPatterns: []
    };
    
    // Find most frequent location
    const locationCounts = {};
    locationHistory.data.forEach(location => {
      const key = `${location.city}_${location.countryCode}`;
      locationCounts[key] = (locationCounts[key] || 0) + location.sessionCount;
    });
    
    const mostFrequent = Object.entries(locationCounts)
      .sort(([,a], [,b]) => b - a)[0];
    
    if (mostFrequent) {
      const [locationKey] = mostFrequent;
      insights.mostFrequentLocation = locationHistory.data
        .find(l => `${l.city}_${l.countryCode}` === locationKey);
    }
    
    return insights;
  } catch (error) {
    console.error('Error getting location insights:', error);
    throw error;
  }
}

/**
 * Enhanced logout with session cleanup
 * @param {boolean} logoutAll - Whether to logout from all devices
 * @param {object} options - Request options
 * @returns {Promise<object>} Logout result
 */
// In ApiService class, replace both logout methods with:
async logout(logoutAll = false, options = {}) {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    const deviceId = this.generateDeviceId();
    
    const data = await this.post('/auth/logout', {
      refreshToken,
      logoutAll,
      deviceId
    }, {
      operation: 'User Logout',
      skipAuthRetry: true,
      ...options
    });
    
    return { 
      success: true,
      message: data.message || 'Logged out successfully'
    };
  } catch (error) {
    console.error('Logout error:', error);
    return { success: false, error: error.message };
  } finally {
    // Always clean up local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    this.clearCache();
  }
}

// =============================================
// UTILITY METHODS FOR SESSION MANAGEMENT
// =============================================

/**
 * Generate session ID for tracking
 * @returns {string} Session ID
 */
generateSessionId() {
  let sessionId = sessionStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
}

/**
 * Get device fingerprint for security tracking
 * @returns {object} Device fingerprint
 */
getDeviceFingerprint() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: {
      width: screen.width,
      height: screen.height,
      colorDepth: screen.colorDepth
    },
    timestamp: new Date().toISOString()
  };
}

/**
 * Check if current session is secure
 * @returns {Promise<boolean>} True if session is secure
 */
async isSessionSecure() {
  try {
    const securityData = await this.getSessionSecurity();
    const { securityStatus } = securityData;
    
    if (!securityStatus) return false;
    
    // Check various security factors
    const isSecure = 
      securityStatus.security.riskLevel === 'low' &&
      !securityStatus.security.requiresVerification &&
      securityStatus.security.riskScore < 30;
    
    return isSecure;
  } catch (error) {
    console.error('Error checking session security:', error);
    return false;
  }
}

/**
 * Get security recommendations for current session
 * @returns {Promise<Array>} Array of security recommendations
 */
async getSecurityRecommendations() {
  try {
    const securityData = await this.getSessionSecurity();
    const recommendations = securityData.securityStatus?.security?.recommendations || [];
    
    return recommendations.map(rec => ({
      type: rec.type,
      message: rec.message,
      priority: rec.priority,
      actionable: true
    }));
  } catch (error) {
    console.error('Error getting security recommendations:', error);
    return [];
  }
}

/**
 * Format session data for display
 * @param {object} session - Session object
 * @returns {object} Formatted session data
 */
formatSessionForDisplay(session) {
  if (!session) return null;
  
  return {
    id: session.id,
    deviceInfo: {
      name: this.getDeviceDisplayName(session.deviceInfo),
      browser: session.deviceInfo.browser,
      os: session.deviceInfo.os,
      type: session.deviceInfo.deviceType
    },
    location: {
      formatted: session.location?.formatted || 'Unknown Location',
      city: session.location?.city,
      country: session.location?.country?.name,
      flag: session.location?.country?.flag,
      riskLevel: session.location?.riskAssessment?.level || 'unknown'
    },
    security: {
      isCurrent: session.isCurrent,
      isVerified: session.isVerified,
      isProxy: session.location?.proxyVpn?.isProxy,
      riskScore: session.location?.riskAssessment?.score || 0
    },
    timestamps: {
      created: session.createdAt,
      lastActive: session.lastActive,
      createdFormatted: this.formatRelativeTime(session.createdAt),
      lastActiveFormatted: this.formatRelativeTime(session.lastActive)
    }
  };
}

/**
 * Get display name for device
 * @param {object} deviceInfo - Device information
 * @returns {string} Display name
 */
getDeviceDisplayName(deviceInfo) {
  if (!deviceInfo) return 'Unknown Device';
  
  const { browser, os, deviceType } = deviceInfo;
  
  if (deviceType === 'Mobile') {
    return `${browser} on ${os} (Mobile)`;
  } else if (deviceType === 'Tablet') {
    return `${browser} on ${os} (Tablet)`;
  } else {
    return `${browser} on ${os}`;
  }
}

/**
 * Check if two locations are suspicious (impossible travel)
 * @param {object} location1 - First location
 * @param {object} location2 - Second location
 * @param {number} timeDiffMs - Time difference in milliseconds
 * @returns {object} Travel analysis
 */
analyzeSuspiciousTravel(location1, location2, timeDiffMs) {
  if (!location1?.coordinates || !location2?.coordinates) {
    return { suspicious: false, reason: 'Missing coordinates' };
  }
  
  // Calculate distance (simplified)
  const distance = this.calculateLocationDistance(location1, location2);
  const timeHours = timeDiffMs / (1000 * 60 * 60);
  const requiredSpeed = distance / timeHours;
  
  // Consider travel suspicious if speed > 1000 km/h (commercial flight speed)
  const maxReasonableSpeed = 1000;
  const suspicious = requiredSpeed > maxReasonableSpeed;
  
  return {
    suspicious,
    distance,
    timeHours: Math.round(timeHours * 100) / 100,
    requiredSpeed: Math.round(requiredSpeed * 100) / 100,
    maxReasonableSpeed,
    reason: suspicious ? `Required speed (${Math.round(requiredSpeed)} km/h) exceeds reasonable travel speed` : null
  };
}

/**
 * Simple distance calculation between two coordinates
 * @param {object} loc1 - Location 1 with coordinates
 * @param {object} loc2 - Location 2 with coordinates
 * @returns {number} Distance in kilometers
 */
calculateLocationDistance(loc1, loc2) {
  const R = 6371; // Earth's radius in km
  const lat1 = loc1.coordinates.latitude * Math.PI / 180;
  const lat2 = loc2.coordinates.latitude * Math.PI / 180;
  const deltaLat = (loc2.coordinates.latitude - loc1.coordinates.latitude) * Math.PI / 180;
  const deltaLon = (loc2.coordinates.longitude - loc1.coordinates.longitude) * Math.PI / 180;
  
  const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(deltaLon/2) * Math.sin(deltaLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  
  return R * c;
}

  // =====================
  // EMAIL HISTORY APIs (Admin)
  // =====================

  async getEmailHistory() {
    try {
      const data = await this.get('/auth/email-history');
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching email history:', error);
      return [];
    }
  }

  // =====================
  // MENU APIs
  // =====================

  async getMenuItems(category = '', includeUnavailable = false, options = {}) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (includeUnavailable) params.append('includeUnavailable', 'true');
      
      // Add any additional query parameters
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value);
          }
        });
      }

      const queryString = params.toString();
      const endpoint = `/menu${queryString ? `?${queryString}` : ''}`;
      
      const config = {
        cache: options.cache !== false, // Default to true unless explicitly false
        timeout: options.timeout || 10000,
        headers: options.headers || {}
      };

      const response = await this.get(endpoint, config);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to fetch menu items', response.status);
      }

      return {
        data: response.data || [],
        meta: response.meta || {},
        success: true
      };
    } catch (error) {
      console.error('Error fetching menu items:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to fetch menu items. Please try again later.', 500);
    }
  }

  async getMenuByCategory(category, options = {}) {
    try {
      if (!category) {
        throw new ValidationError('Category parameter is required');
      }

      const response = await this.getMenuItems(category, false, options);
      
      return {
        ...response,
        category: category,
        count: response.data.length
      };
    } catch (error) {
      console.error(`Error fetching menu items for category ${category}:`, error);
      throw error;
    }
  }

  async getMenuItemById(id, options = {}) {
    try {
      if (!id) {
        throw new ValidationError('Item ID is required');
      }

      const config = {
        cache: options.cache !== false,
        timeout: options.timeout || 8000,
        headers: options.headers || {}
      };

      const response = await this.get(`/menu/${id}`, config);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Menu item not found', response.status || 404);
      }

      return {
        data: response.data || null,
        success: true
      };
    } catch (error) {
      console.error(`Error fetching menu item ${id}:`, error);
      throw error;
    }
  }

  async searchMenuItems(query, filters = {}, options = {}) {
    try {
      if (!query && Object.keys(filters).length === 0) {
        throw new ValidationError('Search query or filters are required');
      }

      const params = new URLSearchParams();
      if (query) params.append('q', query);
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
          } else {
            params.append(key, value);
          }
        }
      });

      const config = {
        cache: options.cache || false, // Typically don't cache search results
        timeout: options.timeout || 15000,
        headers: options.headers || {}
      };

      const response = await this.get(`/menu/search?${params.toString()}`, config);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Search failed', response.status);
      }

      return {
        data: response.data || [],
        meta: response.meta || {},
        query,
        filters,
        success: true
      };
    } catch (error) {
      console.error('Error searching menu items:', error);
      throw error;
    }
  }


  async getTopOrderedItems(limit = 10) {
    try {
      const response = await this.get(`/menu/top-ordered?limit=${limit}`, { cache: true, timeout: 8000 });
      return response.success ? (response.data || []) : [];
    } catch (_) { return []; }
  }

  async getFeaturedMenuItems(limit = 5, options = {}) {
    try {
      const response = await this.get(`/menu/featured?limit=${limit}`, {
        cache: options.cache !== false,
        timeout: options.timeout || 10000
      });
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to fetch featured items', response.status);
      }

      return {
        data: response.data || [],
        success: true
      };
    } catch (error) {
      console.error('Error fetching featured menu items:', error);
      throw error;
    }
  }

  async getMenuCategories(options = {}) {
    try {
      const response = await this.get('/menu/categories', {
        cache: true, // Categories don't change often
        timeout: options.timeout || 5000
      });
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to fetch categories', response.status);
      }

      return {
        categories: response.data || [],
        count: response.data?.length || 0,
        success: true
      };
    } catch (error) {
      console.error('Error fetching menu categories:', error);
      throw error;
    }
  }

  async getDailySpecials(date = new Date(), options = {}) {
    try {
      const dateString = date.toISOString().split('T')[0];
      const response = await this.get(`/menu/specials?date=${dateString}`, {
        cache: options.cache !== false,
        timeout: options.timeout || 8000
      });
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to fetch daily specials', response.status);
      }

      return {
        date: dateString,
        items: response.data || [],
        success: true
      };
    } catch (error) {
      console.error('Error fetching daily specials:', error);
      throw error;
    }
  }

  // =====================
  // FAVORITE ITEMS APIs 
  // =====================

  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    return token && token !== 'null' && token !== 'undefined';
  }

  // Updated getFavoriteItems method with authentication check
  async getFavoriteItems(options = {}) {
    try {
      // Check if user is authenticated before making the request
      if (!this.isAuthenticated()) {
        // Return empty result for unauthenticated users instead of throwing error
        return {
          data: [],
          meta: {},
          total: 0,
          currentPage: 1,
          totalPages: 1,
          success: true,
          isAuthenticated: false
        };
      }

      const params = new URLSearchParams();
      if (options.category) params.append('category', options.category);
      if (options.search) params.append('search', options.search);
      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      
      const queryString = params.toString();
      const endpoint = `/favorites${queryString ? `?${queryString}` : ''}`;
      
      const config = {
        cache: options.cache !== false,
        timeout: options.timeout || 10000,
        headers: options.headers || {}
      };

      const response = await this.get(endpoint, config);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to fetch favorite items', response.status);
      }

      return {
        data: response.data || [],
        meta: response.meta || {},
        total: response.total || 0,
        currentPage: response.currentPage || 1,
        totalPages: response.totalPages || 1,
        success: true,
        isAuthenticated: true
      };
    } catch (error) {
      console.error('Error fetching favorite items:', error);
      
      // Return empty result for 401 errors instead of throwing
      if (error instanceof ApiError && error.status === 401) {
        return {
          data: [],
          meta: {},
          total: 0,
          currentPage: 1,
          totalPages: 1,
          success: true,
          isAuthenticated: false
        };
      }
      
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to fetch favorite items. Please try again later.', 500);
    }
  }

  // Updated addToFavorites method with authentication check
  async addToFavorites(menuItemId) {
    try {
      // Check authentication before attempting to add favorites
      if (!this.isAuthenticated()) {
        throw new ApiError('Please log in to add favorites.', 401);
      }

      console.log('🔍 DEBUG - Adding to favorites, itemId:', menuItemId);
      
      if (!menuItemId) {
        throw new ValidationError('Menu item ID is required');
      }

      const requestBody = { 
        menuItemId: menuItemId.toString() 
      };
      
      console.log('🔍 DEBUG - Request body being sent:', requestBody);

      const response = await this.post('/favorites', requestBody);
      
      console.log('🔍 DEBUG - Server response:', response);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to add to favorites', response.status);
      }

      return {
        success: true,
        data: response.data,
        message: response.message
      };
    } catch (error) {
      console.error('❌ Error adding to favorites:', error);
      throw error;
    }
  }

  // Updated removeFromFavorites method with authentication check
  async removeFromFavorites(itemId) {
    try {
      // Check authentication before attempting to remove favorites
      if (!this.isAuthenticated()) {
        throw new ApiError('Please log in to manage favorites.', 401);
      }

      console.log('🔍 DEBUG - Removing from favorites, itemId:', itemId);
      
      if (!itemId) {
        throw new ValidationError('Item ID is required');
      }

      const response = await this.delete(`/favorites/${itemId}`);
      
      console.log('🔍 DEBUG - Remove response:', response);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to remove from favorites', response.status);
      }

      return {
        success: true,
        message: response.message
      };
    } catch (error) {
      console.error('❌ Error removing from favorites:', error);
      throw error;
    }
  }

  // Add this new method to check if an item is favorited:
  async checkFavoriteStatus(itemId) {
    try {
      if (!itemId) {
        throw new ValidationError('Item ID is required');
      }

      const response = await this.get(`/favorites/check/${itemId}`);
      
      return {
        isFavorite: response.isFavorite || false,
        favoriteId: response.favoriteId || null,
        success: true
      };
    } catch (error) {
      console.error('Error checking favorite status:', error);
      // Don't throw error for this method, just return false
      return {
        isFavorite: false,
        favoriteId: null,
        success: false
      };
    }
  }

  // Add this method to get favorites count:
  async getFavoritesCount() {
    try {
      const response = await this.get('/favorites/count');
      
      return {
        count: response.count || 0,
        success: true
      };
    } catch (error) {
      console.error('Error getting favorites count:', error);
      return {
        count: 0,
        success: false
      };
    }
  }

  // =====================
  // ORDER APIs
  // =====================


  // FEATURE: customer post-order amendments.
  async customerAddItemsToOrder(orderId, items, sessionId) {
    const headers = sessionId ? { 'X-Session-Id': sessionId } : {};
    return this.post(`/orders/${orderId}/items-customer`, { items }, { headers });
  }
  async customerUpdateOrderLine(orderId, lineId, payload, sessionId) {
    const headers = sessionId ? { 'X-Session-Id': sessionId } : {};
    return this.patch(`/orders/${orderId}/items/${lineId}`, payload, { headers });
  }
  async customerSendOrderRequest(orderId, type, text, sessionId, actorName) {
    const headers = sessionId ? { 'X-Session-Id': sessionId } : {};
    return this.post(`/orders/${orderId}/requests`, { type, text, actorName }, { headers });
  }
  async customerListOrderRequests(orderId, sessionId) {
    const headers = sessionId ? { 'X-Session-Id': sessionId } : {};
    return this.get(`/orders/${orderId}/requests`, { headers });
  }

  async createOrder(orderData) {
    try {
      // AUDIT FIX U-4: forward Idempotency-Key as a header too — backend
      // checks both body.idempotencyKey and the Idempotency-Key header.
      const headers = orderData?.idempotencyKey ? { 'Idempotency-Key': String(orderData.idempotencyKey) } : {};
      const data = await this.post('/orders', orderData, { headers });
      this.clearCache('/orders');
      return data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  async getOrderById(orderId) {
    try {
      const data = await this.get(`/orders/${orderId}`);
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching order:', error);
      return null;
    }
  }

  async getUserOrders(page = 1, limit = 10, status = '') {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (status) params.append('status', status);
      
      const data = await this.get(`/orders/history?${params.toString()}`);
      return data.success ? data : { data: [], total: 0, currentPage: 1, totalPages: 1 };
    } catch (error) {
      console.error('Error fetching user orders:', error);
      return { data: [], total: 0, currentPage: 1, totalPages: 1 };
    }
  }

  async trackOrder(orderNumber, sessionId = '') {
    // Public endpoint — sanitized order projection. Pass sessionId for
    // guest orders (came in via the link in the confirmation email).
    // Returns the data object on success, or throws an Error with the
    // server message so callers can render different states (404 vs 403).
    const qs = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : '';
    const data = await this.get(`/orders/track/${encodeURIComponent(orderNumber)}${qs}`, {
      operation: 'Track Order', cache: false,
    });
    if (!data || !data.success) {
      const err = new Error(data?.message || 'Order not found');
      err.code = data?.code;
      throw err;
    }
    return data.data;
  }

  async cancelOrder(orderId, reason = 'Cancelled by customer') {
    try {
      const data = await this.patch(`/orders/${orderId}/cancel`, { reason });
      this.clearCache('/orders');
      return data;
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    }
  }

  async reorderItems(orderId) {
    try {
      const data = await this.post(`/orders/${orderId}/reorder`);
      return data;
    } catch (error) {
      console.error('Error reordering items:', error);
      throw error;
    }
  }

  // =====================
  // RESERVATION APIs
  // =====================

  async createReservation(reservationData) {
    try {
      const data = await this.post('/reservations', reservationData);
      this.clearCache('/reservations');
      return data;
    } catch (error) {
      console.error('Error creating reservation:', error);
      throw error;
    }
  }

  async getReservationById(reservationId) {
    try {
      const data = await this.get(`/reservations/${reservationId}`);
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching reservation:', error);
      return null;
    }
  }

  async getUserReservations(page = 1, limit = 10) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      const response = await this.get(`/reservations/history?${params.toString()}`);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to fetch reservations', response.status);
      }
      
      return {
        data: response.data || [],
        total: response.total || 0,
        currentPage: response.page || 1,
        totalPages: response.pages || 1,
        success: true
      };
    } catch (error) {
      console.error('Error fetching user reservations:', error);
      if (error.status === 401) {
        throw new ApiError('Please log in to view your reservations', 401);
      }
      throw error;
    }
  }

  // Also add this method for guest reservations lookup:
  async findReservationsByContact(email, phone) {
    try {
      const params = new URLSearchParams();
      if (email) params.append('email', email);
      if (phone) params.append('phone', phone);
      
      const response = await this.get(`/reservations/history?${params.toString()}`);
      
      if (!response.success) {
        throw new ApiError(response.message || 'Failed to fetch reservations', response.status);
      }
      
      return {
        data: response.data || [],
        total: response.total || 0,
        success: true
      };
    } catch (error) {
      console.error('Error fetching reservations by contact:', error);
      throw error;
    }
  }

  async cancelReservation(reservationId, reason = 'Cancelled by customer') {
    try {
      const data = await this.patch(`/reservations/${reservationId}/cancel`, { reason });
      this.clearCache('/reservations');
      return data;
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      throw error;
    }
  }

  async checkAvailability(date, time, guests) {
    try {
      const data = await this.post('/reservations/availability', { date, time, guests });
      return data;  // ✅ Return the whole response which has { success, available, remainingCapacity, ... }
    } catch (error) {
      console.error('Error checking availability:', error);
      return { available: true };  // Fallback to allow submission
    }
  }

  async getAvailableTimeSlots(date, guests) {
    try {
      const data = await this.get(`/reservations/time-slots?date=${date}&guests=${guests}`, { cache: true });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching time slots:', error);
      return [];
    }
  }

  async modifyReservation(reservationId, updates) {
    try {
      const data = await this.patch(`/reservations/${reservationId}`, updates);
      this.clearCache('/reservations');
      return data;
    } catch (error) {
      console.error('Error modifying reservation:', error);
      throw error;
    }
  }

  // =====================
  // TABLE APIs
  // =====================

  async getTableInfo(tableNumber) {
    try {
      const data = await this.get(`/tables/info/${tableNumber}`, { cache: true });
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching table info:', error);
      return null;
    }
  }

  async getAvailableTables(date, time, guests) {
    try {
      const params = new URLSearchParams({ date, time, guests: guests.toString() });
      const data = await this.get(`/tables/available?${params.toString()}`);
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching available tables:', error);
      return [];
    }
  }

  // =====================
  // REVIEW & RATING APIs
  // =====================

  async submitReview(bookingId, bookingType, reviewData) {
    try {
      const endpoint = bookingType === 'order' ? 'orders' : 'reservations';
      const data = await this.post(`/${endpoint}/${bookingId}/review`, {
        ...reviewData,
        bookingType,
      });
      return data;
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }

  async getReviews(itemId, page = 1, limit = 10) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const data = await this.get(`/reviews/${itemId}?${params.toString()}`, { cache: true });
      return data.success ? data : { data: [], total: 0, averageRating: 0 };
    } catch (error) {
      console.error('Error fetching reviews:', error);
      return { data: [], total: 0, averageRating: 0 };
    }
  }

  // =====================
  // CONTACT & FEEDBACK APIs
  // =====================

  async sendContactMessage(messageData) {
    try {
      return await this.post('/contact', messageData);
    } catch (error) {
      console.error('Error sending contact message:', error);
      throw error;
    }
  }

  async submitFeedback(feedbackData) {
    try {
      return await this.post('/feedback', feedbackData);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  // =====================
  // RESTAURANT INFO APIs
  // =====================

  async getRestaurantInfo() {
    try {
      const data = await this.get('/restaurant/info', { cache: true });
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching restaurant info:', error);
      return null;
    }
  }

  // Public settings — flat groups (general / operations / payment / menu …).
  // Driven by the admin Settings page. Cached so it only hits the network once.
  async getPublicSettings() {
    try {
      const data = await this.get('/settings/public', { cache: true });
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching public settings:', error);
      return null;
    }
  }

  async getOperatingHours() {
    try {
      const data = await this.get('/restaurant/hours', { cache: true });
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching operating hours:', error);
      return null;
    }
  }

  async getSpecialOffers() {
    try {
      const data = await this.get('/restaurant/offers', { cache: true });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching special offers:', error);
      return [];
    }
  }

  // =====================
  // NEWSLETTER & SUBSCRIPTION APIs
  // =====================

  async subscribeNewsletter(email) {
    try {
      return await this.post('/newsletter/subscribe', { email });
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      throw error;
    }
  }

  async unsubscribeNewsletter(email, token) {
    try {
      return await this.post('/newsletter/unsubscribe', { email, token });
    } catch (error) {
      console.error('Error unsubscribing from newsletter:', error);
      throw error;
    }
  }

  // =====================
  // NOTIFICATION APIs
  // =====================

  async getNotifications(page = 1, limit = 20, unreadOnly = false) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (unreadOnly) params.append('unreadOnly', 'true');
      
      const data = await this.get(`/notifications?${params.toString()}`);
      return data.success ? data : { data: [], total: 0, unreadCount: 0 };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { data: [], total: 0, unreadCount: 0 };
    }
  }

  async markNotificationRead(notificationId) {
    try {
      return await this.patch(`/notifications/${notificationId}/read`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markAllNotificationsRead() {
    try {
      return await this.patch('/notifications/read-all');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // =====================
  // LOYALTY & REWARDS APIs
  // =====================

  async getLoyaltyPoints() {
    try {
      const data = await this.get('/loyalty/points');
      return data.success ? data.data : { points: 0, tier: 'bronze' };
    } catch (error) {
      console.error('Error fetching loyalty points:', error);
      return { points: 0, tier: 'bronze' };
    }
  }

  async getRewards() {
    try {
      const data = await this.get('/loyalty/rewards', { cache: true });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching rewards:', error);
      return [];
    }
  }

  async redeemReward(rewardId) {
    try {
      const data = await this.post(`/loyalty/rewards/${rewardId}/redeem`);
      this.clearCache('/loyalty');
      return data;
    } catch (error) {
      console.error('Error redeeming reward:', error);
      throw error;
    }
  }

  // =====================
  // UTILITY & HELPER METHODS
  // =====================

  // Data formatting methods
  formatOrderData(cart, customerInfo, orderType = 'takeaway', tableNumber = null) {
    return {
      customerInfo: {
        name: customerInfo.name || 'Guest Customer',
        email: customerInfo.email || '',
        phone: customerInfo.phone || '',
      },
      items: cart.map(item => ({
        menuItem: item._id || item.id,
        quantity: item.quantity,
        specialInstructions: item.specialInstructions || '',
        price: item.price,
      })),
      orderType,
      tableNumber,
      specialInstructions: customerInfo.specialInstructions || '',
      source: 'web',
    };
  }

  formatReservationData(reservationForm) {
    return {
      customerInfo: {
        name: reservationForm.name,
        email: reservationForm.email,
        phone: reservationForm.phone,
      },
      date: reservationForm.date,
      time: reservationForm.time,
      guests: parseInt(reservationForm.guests),
      specialRequests: reservationForm.specialRequests || '',
      source: 'web',
    };
  }

  formatBookingForDisplay(booking, type) {
    const baseFormatting = {
      id: booking._id,
      bookingType: type,
      status: booking.status,
      createdAt: booking.createdAt,
      customerInfo: booking.customerInfo,
    };

    if (type === 'order') {
      return {
        ...baseFormatting,
        orderNumber: booking.orderNumber,
        orderType: booking.orderType,
        items: booking.items,
        pricing: booking.pricing,
        tableNumber: booking.tableNumber,
        specialInstructions: booking.specialRequests,
        estimatedDeliveryTime: booking.estimatedDeliveryTime,
      };
    } else {
      return {
        ...baseFormatting,
        reservationNumber: booking.reservationNumber,
        date: booking.date,
        time: booking.time,
        guests: booking.guests,
        tableNumber: booking.tableNumber,
        specialRequests: booking.specialRequests,
      };
    }
  }

  // Business logic methods
  canCancelBooking(booking) {
    if (!booking || ['cancelled', 'completed'].includes(booking.status)) {
      return { canCancel: false, reason: 'Booking is already cancelled or completed' };
    }

    if (booking.bookingType === 'order') {
      const canCancel = ['pending', 'confirmed'].includes(booking.status);
      return {
        canCancel,
        reason: canCancel ? null : 'Order is already being prepared or has been served',
      };
    } else {
      const bookingDate = new Date(booking.date);
      const now = new Date();
      const timeDiff = bookingDate.getTime() - now.getTime();
      const hoursDiff = timeDiff / (1000 * 3600);
      
      const canCancel = booking.status === 'confirmed' && hoursDiff > 24;
      return {
        canCancel,
        reason: canCancel ? null : 'Reservations can only be cancelled 24 hours in advance',
      };
    }
  }

  calculateTotal(cart, tax = 0, delivery = 0, discount = 0) {
    const subtotal = cart.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
    
    const taxAmount = (subtotal * tax) / 100;
    const total = subtotal + taxAmount + delivery - discount;
    
    return {
      subtotal,
      tax: taxAmount,
      delivery,
      discount,
      total: Math.max(0, total),
    };
  }

  isRestaurantOpen(operatingHours = null) {
    if (!operatingHours) return true;
    
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'lowercase' });
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    const todayHours = operatingHours[currentDay];
    if (!todayHours || todayHours.closed) return false;
    
    const openTime = parseInt(todayHours.open.replace(':', ''));
    const closeTime = parseInt(todayHours.close.replace(':', ''));
    
    return currentTime >= openTime && currentTime <= closeTime;
  }

  // Validation methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone) {
    const phoneRegex = /^[\+]?[\d\s\-\(\)]{8,}$/;
    return phoneRegex.test(phone);
  }

  validateBookingData(bookingData, type) {
    const errors = {};
    
    if (type === 'reservation') {
      if (!bookingData.date) errors.date = 'Date is required';
      if (!bookingData.time) errors.time = 'Time is required';
      if (!bookingData.guests || bookingData.guests < 1) {
        errors.guests = 'Number of guests is required';
      }
    }
    
    if (!bookingData.customerInfo?.name) errors.name = 'Name is required';
    if (!bookingData.customerInfo?.email) {
      errors.email = 'Email is required';
    } else if (!this.isValidEmail(bookingData.customerInfo.email)) {
      errors.email = 'Valid email is required';
    }
    if (!bookingData.customerInfo?.phone) {
      errors.phone = 'Phone is required';
    } else if (!this.isValidPhone(bookingData.customerInfo.phone)) {
      errors.phone = 'Valid phone number is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Formatting methods
  // Legacy entry point — delegate to the single source of truth in utils so
  // every formatter in the app emits the same "PKR 3,450.00" shape. Prefer
  // useApp().formatCurrency in components; this method only exists for any
  // older non-React caller.
  formatCurrency(amount, currency) {
    return sharedFormatCurrency(amount, currency);
  }

  formatDate(date, options = {}) {
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    
    return new Date(date).toLocaleDateString('en-US', { ...defaultOptions, ...options });
  }

  formatTime(time, format12Hour = true) {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: format12Hour,
    });
  }

  formatRelativeTime(date) {
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return this.formatDate(date);
  }

  getStatusDisplayInfo(status) {
    const statusMap = {
      'pending': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '⏱️', label: 'Pending' },
      'confirmed': { color: 'bg-green-100 text-green-800 border-green-200', icon: '✅', label: 'Confirmed' },
      'preparing': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '👨‍🍳', label: 'Preparing' },
      'ready': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: '🔔', label: 'Ready' },
      'served': { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: '🍽️', label: 'Served' },
      'out-for-delivery': { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: '🚚', label: 'Out for Delivery' },
      'completed': { color: 'bg-green-100 text-green-800 border-green-200', icon: '✅', label: 'Completed' },
      'cancelled': { color: 'bg-red-100 text-red-800 border-red-200', icon: '❌', label: 'Cancelled' },
      'seated': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '👥', label: 'Seated' },
      'no-show': { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '👻', label: 'No Show' },
    };
    
    return statusMap[status] || statusMap['pending'];
  }

  // Utility methods
  generateOrderNumber() {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `SCR${timestamp}${random}`;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Device and browser detection
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);
    const isDesktop = !isMobile && !isTablet;
    
    return {
      isMobile,
      isTablet,
      isDesktop,
      userAgent,
      platform: navigator.platform,
      language: navigator.language,
    };
  }

  generateDeviceId() {
    // Generate a unique device ID for session tracking
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  // Health check
  async healthCheck() {
    try {
      const data = await this.get('/health', { timeout: 5000 });
      return data;
    } catch (error) {
      console.error('Health check failed:', error);
      return { success: false, error: error.message };
    }
  }
}

// =============================================
// ADMIN API SERVICE CLASS
// =============================================

// Coerce a value (Date | string | number) to YYYY-MM-DD for API query params.
function _isoDate(v) {
  if (!v) return '';
  if (typeof v === 'string') return v.length >= 10 ? v.slice(0, 10) : v;
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

class AdminApiService extends ApiService {
  constructor() {
    super();
    this.cacheTimeout = 2 * 60 * 1000; // 2 minutes for admin data
    this.wsConnection = null;
    this.eventListeners = new Map();
  }

  // =====================
  // CORE HTTP METHODS (Override for Admin)
  // =====================

  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      timeout = 45000,
      cache = false,
      retries = 3,
      operation = `${method} ${endpoint}`,
    } = options;

    const url = `${this.baseURL}${endpoint}`;
    // AUDIT FIX P-9: only build a cache key for GETs; non-GET bodies
    // would otherwise live in the in-memory cache map indefinitely.
    const cacheKey = method === 'GET' ? `GET:${url}` : null;

    // Check cache for GET requests
    if (method === 'GET' && cache) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;
    }

    const { controller, cleanup } = AdminRequestConfig.createAbortController(timeout);

    try {
      const requestFn = async () => {
        const fetchOptions = AdminRequestConfig.createFetchOptions(method, body, headers, controller.signal);
        const response = await fetch(url, fetchOptions);
        const data = await AdminResponseInterceptor.handleResponse(response, operation);
        
        // Cache successful GET requests
        if (method === 'GET' && cache && data) {
          this.setCache(cacheKey, data);
        }
        
        return data;
      };

      return await AdminRequestManager.retryRequest(requestFn, operation, retries);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new AdminNetworkError('Request timeout', operation);
      }
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new AdminNetworkError('Network error - please check your connection', operation);
      }
      throw error;
    } finally {
      cleanup();
    }
  }

  // =====================
  // AUTHENTICATION APIs (Using Main Auth System)
  // =====================

  async login(credentials) {
    try {
      // Use the main auth login endpoint
      const data = await this.post('/auth/login', credentials, {
        operation: 'Admin Login',
        retries: 1, // Don't retry login attempts
      });
      
      if (data.success || data.accessToken) {
        // Check if user has admin privileges
        if (!data.user || !['admin', 'manager', 'staff'].includes(data.user.role)) {
          // Clear tokens if not admin
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          throw new AdminApiError('Insufficient privileges. Admin access required.', 403);
        }

        // Store tokens using main auth keys
        if (data.accessToken) {
          localStorage.setItem('authToken', data.accessToken);
        }
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        
        this.clearCache();
        return { success: true, user: data.user };
      }
      
      throw new AdminApiError(data.message || 'Login failed', 401);
    } catch (error) {
      console.error('Admin login error:', error);
      throw error;
    }
  }

  async validateToken() {
    try {
      const data = await this.get('/auth/verify', { 
        operation: 'Token Validation',
        cache: false,
      });
      
      // Check if user has admin privileges
      if (data.user && ['admin', 'manager', 'staff'].includes(data.user.role)) {
        return { success: true, user: data.user };
      }
      
      return { success: false, message: 'Insufficient privileges' };
    } catch (error) {
      console.error('Token validation error:', error);
      return { success: false };
    }
  }

  // =====================
  // DASHBOARD APIs
  // =====================

  async getDashboardStats(timeRange = '24h') {
    try {
      const data = await this.get(`/admin/dashboard/stats?range=${timeRange}`, {
        operation: 'Get Dashboard Stats',
        cache: true,
      });
      return data.success ? data.data : {};
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {};
    }
  }

  async getRealtimeStats() {
    try {
      const data = await this.get('/admin/dashboard/realtime', {
        operation: 'Get Realtime Stats',
        cache: false,
      });
      return data.success ? data.data : {};
    } catch (error) {
      console.error('Error fetching realtime stats:', error);
      return {};
    }
  }

  async getRecentOrders(limit = 10, status = '') {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      if (status) params.append('status', status);

      // Hit the admin endpoint — the public /orders endpoint scopes results
      // to req.user._id, so when an admin is logged in it would return only
      // the admin's *own* orders (almost always empty).
      const data = await this.get(`/admin/orders?${params.toString()}`, {
        operation: 'Get Recent Orders',
        cache: false,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      return [];
    }
  }

  async getTableStatus() {
    try {
      const data = await this.get('/tables/status', {
        operation: 'Get Table Status',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching table status:', error);
      return [];
    }
  }

  async getPerformanceMetrics(timeRange = '7d') {
    try {
      const data = await this.get(`/admin/dashboard/performance?range=${timeRange}`, {
        operation: 'Get Performance Metrics',
        cache: true,
      });
      return data.success ? data.data : {};
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      return {};
    }
  }

  // =====================
  // MENU MANAGEMENT APIs
  // =====================

  async createMenuItem(menuItem) {
    try {
      const data = await this.post('/menu', menuItem, {
        operation: 'Create Menu Item',
      });
      this.clearCache('/menu');
      return data;
    } catch (error) {
      console.error('Error creating menu item:', error);
      throw error;
    }
  }

  async updateMenuItem(id, menuItem) {
    try {
      const data = await this.put(`/menu/${id}`, menuItem, {
        operation: 'Update Menu Item',
      });
      this.clearCache('/menu');
      return data;
    } catch (error) {
      console.error('Error updating menu item:', error);
      throw error;
    }
  }

  // Toggle availability only. Uses the dedicated PATCH route gated by
  // menu:toggle (admin/manager/chef) — separate from menu:update which is
  // restricted to admin/manager. Returns the updated MenuItem.
  async setMenuItemAvailability(id, isAvailable) {
    try {
      const data = await this.patch(`/menu/${id}/availability`, { isAvailable: !!isAvailable }, {
        operation: 'Set Menu Item Availability',
      });
      this.clearCache('/menu');
      if (data && data.success === false) throw new Error(data.message || 'Failed');
      return data?.data || data;
    } catch (error) {
      console.error('Error setting menu item availability:', error);
      throw error;
    }
  }

  async toggleMenuItemAvailability(id) {
    try {
      const data = await this.patch(`/menu/${id}/toggle-availability`, null, {
        operation: 'Toggle Menu Item Availability',
      });
      this.clearCache('/menu');
      return data;
    } catch (error) {
      console.error('Error toggling menu item availability:', error);
      throw error;
    }
  }

  async deleteMenuItem(id) {
    try {
      const data = await this.delete(`/menu/${id}`, {
        operation: 'Delete Menu Item',
      });
      this.clearCache('/menu');
      return data;
    } catch (error) {
      console.error('Error deleting menu item:', error);
      throw error;
    }
  }

  async uploadMenuItemImage(id, imageFile) {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const data = await this.post(`/menu/${id}/image`, formData, {
        operation: 'Upload Menu Item Image',
        headers: {}, // Let browser set content-type for FormData
      });
      this.clearCache('/menu');
      return data;
    } catch (error) {
      console.error('Error uploading menu item image:', error);
      throw error;
    }
  }

  async bulkUpdateMenuItems(updates) {
    try {
      const data = await this.patch('/menu/bulk-update', { updates }, {
        operation: 'Bulk Update Menu Items',
      });
      this.clearCache('/menu');
      return data;
    } catch (error) {
      console.error('Error bulk updating menu items:', error);
      throw error;
    }
  }

  async duplicateMenuItem(id) {
    try {
      const data = await this.post(`/menu/${id}/duplicate`, null, {
        operation: 'Duplicate Menu Item',
      });
      this.clearCache('/menu');
      return data;
    } catch (error) {
      console.error('Error duplicating menu item:', error);
      throw error;
    }
  }

  // =====================
  // TABLE MANAGEMENT APIs
  // =====================

  async getTables() {
    try {
      const data = await this.get('/tables', {
        operation: 'Get Tables',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching tables:', error);
      return [];
    }
  }

  async createTable(tableData) {
    try {
      const data = await this.post('/admin/tables', tableData, {
        operation: 'Create Table',
      });
      this.clearCache('/tables');
      return data;
    } catch (error) {
      console.error('Error creating table:', error);
      throw error;
    }
  }

  async updateTable(id, tableData) {
    try {
      const data = await this.put(`/tables/${id}`, tableData, {
        operation: 'Update Table',
      });
      this.clearCache('/tables');
      return data;
    } catch (error) {
      console.error('Error updating table:', error);
      throw error;
    }
  }

  async deleteTable(id) {
    try {
      const data = await this.delete(`/tables/${id}`, {
        operation: 'Delete Table',
      });
      this.clearCache('/tables');
      return data;
    } catch (error) {
      console.error('Error deleting table:', error);
      throw error;
    }
  }

  async updateTableStatus(id, status) {
    try {
      const data = await this.patch(`/tables/${id}/status`, { status }, {
        operation: 'Update Table Status',
      });
      this.clearCache('/tables');
      return data;
    } catch (error) {
      console.error('Error updating table status:', error);
      throw error;
    }
  }

  async generateTableQRCode(id) {
    try {
      const response = await fetch(`${this.baseURL}/tables/${id}/qr-code`, {
        headers: AdminRequestInterceptor.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new AdminApiError(`Failed to generate QR code: ${response.statusText}`, response.status);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw error;
    }
  }

  /**
   * Open the branded QR poster for a table in a new tab. Auth-aware
   * (Authorization: Bearer token attached via _fetchBlob), opens the result
   * as a blob URL so the auth header is preserved across the navigation.
   * The poster page auto-fires window.print() — admin just hits Print or
   * "Save as PDF" in the dialog.
   */
  async openQRPoster(tableId) {
    const blob = await this._fetchBlob(`/admin/tables/${tableId}/qr-poster`, 'text/html');
    const htmlBlob = blob.type ? blob : new Blob([blob], { type: 'text/html' });
    const url = URL.createObjectURL(htmlBlob);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return url;
  }

  async downloadQRCode(tableId, tableName) {
    try {
      const blob = await this.generateTableQRCode(tableId);

      // The backend now returns a branded SVG (restaurant short-name
      // overlaid in the centre). If the blob is something else, fall
      // back to a generic extension.
      const ext = (blob && blob.type && /svg/.test(blob.type)) ? 'svg'
                : (blob && blob.type && /png/.test(blob.type)) ? 'png'
                : 'svg';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `table-${tableName}-qr.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading QR code:', error);
      throw error;
    }
  }

  async bulkGenerateQRCodes() {
    try {
      const data = await this.post('/tables/bulk-qr', null, {
        operation: 'Bulk Generate QR Codes',
      });
      return data;
    } catch (error) {
      console.error('Error bulk generating QR codes:', error);
      throw error;
    }
  }

  // =====================
  // ORDER MANAGEMENT APIs
  // =====================

  async getAllOrders(filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      // Add pagination
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc' } = pagination;
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      // Use the admin endpoint so we see ALL orders, not just the current
      // user's. Public /orders is scoped to req.user._id.
      const url = `/admin/orders${params.toString() ? '?' + params.toString() : ''}`;
      const data = await this.get(url, {
        operation: 'Get All Orders',
        cache: false, // Orders change frequently
      });
      return data.success ? data : { data: [], total: 0, currentPage: 1, totalPages: 1 };
    } catch (error) {
      console.error('Error fetching orders:', error);
      return { data: [], total: 0, currentPage: 1, totalPages: 1 };
    }
  }

  // =====================
  // INVOICE APIs
  // =====================

  async getInvoices(filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '') params.append(k, String(v));
      });
      const { page = 1, limit = 20, sortBy = 'issueDate', sortOrder = 'desc' } = pagination;
      params.append('page', String(page));
      params.append('limit', String(limit));
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      const url = `/admin/invoices${params.toString() ? '?' + params.toString() : ''}`;
      const data = await this.get(url, { operation: 'List Invoices', cache: false });
      return data.success
        ? data
        : { data: [], total: 0, currentPage: 1, totalPages: 1 };
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return { data: [], total: 0, currentPage: 1, totalPages: 1 };
    }
  }

  async getInvoice(id) {
    try {
      const data = await this.get(`/admin/invoices/${id}`, { operation: 'Get Invoice', cache: false });
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      return null;
    }
  }

  async createCustomInvoice(payload) {
    const data = await this.post('/admin/invoices', payload, { operation: 'Create Invoice' });
    if (!data.success) throw new Error(data.message || 'Failed to create invoice');
    return data.data;
  }

  async createInvoiceFromOrder(orderId, opts = {}) {
    const data = await this.post(`/admin/invoices/from-order/${orderId}`, opts, { operation: 'Create Invoice From Order' });
    if (!data.success) throw new Error(data.message || 'Failed to create invoice from order');
    return data.data;
  }

  async updateInvoiceStatus(id, status, paymentMethod) {
    const data = await this.patch(`/admin/invoices/${id}/status`, { status, paymentMethod }, { operation: 'Update Invoice Status' });
    if (!data.success) throw new Error(data.message || 'Failed to update status');
    return data.data;
  }

  async updateInvoice(id, payload) {
    const data = await this.patch(`/admin/invoices/${id}`, payload, { operation: 'Update Invoice' });
    if (!data.success) throw new Error(data.message || 'Failed to update invoice');
    return data.data;
  }

  async deleteInvoice(id) {
    const data = await this.delete(`/admin/invoices/${id}`, { operation: 'Delete Invoice' });
    if (!data.success) throw new Error(data.message || 'Failed to delete invoice');
    return data;
  }

  async emailInvoice(id, to) {
    const data = await this.post(`/admin/invoices/${id}/email`, to ? { to } : {}, { operation: 'Email Invoice' });
    if (!data.success) throw new Error(data.message || 'Failed to email invoice');
    return data.data;
  }

  // URL builders kept for callers that just need the absolute path string
  // (e.g. logging, copy-link). NOTE: opening these directly via window.open()
  // will 401 — the admin endpoints require an Authorization: Bearer token,
  // which a fresh navigation does not carry. Use openInvoiceHtml /
  // downloadInvoicePdf below to fetch with auth and surface a blob URL.
  // Date input → 'YYYY-MM-DD' (used by report query params).
  // Placed at module scope below the class to avoid bloating the class body.

  invoiceHtmlUrl(id) {
    const base = (this.baseURL || '').replace(/\/$/, '');
    return `${base}/admin/invoices/${id}/html`;
  }
  invoicePdfUrl(id, { inline = false } = {}) {
    const base = (this.baseURL || '').replace(/\/$/, '');
    return `${base}/admin/invoices/${id}/pdf${inline ? '?inline=1' : ''}`;
  }

  // Internal helper: authenticated fetch that returns a Blob.
  // Throws on non-2xx so the caller can showToast() the error.
  async _fetchBlob(endpoint, accept) {
    const url = `${this.baseURL.replace(/\/$/, '')}${endpoint}`;
    const headers = {
      'Accept': accept,
      ...AdminRequestInterceptor.getAuthHeaders(),
    };
    const res = await fetch(url, { method: 'GET', headers, credentials: 'include' });
    if (!res.ok) {
      // Best-effort decode of JSON error
      let msg = `${res.status} ${res.statusText}`;
      try {
        const txt = await res.text();
        try { msg = JSON.parse(txt).message || JSON.parse(txt).error?.message || msg; }
        catch { if (txt) msg = txt.slice(0, 200); }
      } catch {}
      throw new Error(msg);
    }
    return await res.blob();
  }

  /**
   * Authenticated HTML preview for an invoice.
   * Returns an object URL (blob:…) that can be opened in a new tab.
   * The caller is responsible for revoking it when no longer needed
   * (we auto-revoke after 60s as a safety net).
   */
  async openInvoiceHtml(id) {
    const blob = await this._fetchBlob(`/admin/invoices/${id}/html`, 'text/html');
    // Browsers won't render an octet-stream as HTML — make sure the type sticks.
    const htmlBlob = blob.type ? blob : new Blob([blob], { type: 'text/html' });
    const url = URL.createObjectURL(htmlBlob);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return url;
  }

  /**
   * Authenticated PDF download for an invoice. Triggers a Save-As via a
   * hidden anchor with the `download` attribute. Returns the suggested
   * filename so callers can showToast it.
   */
  async downloadInvoicePdf(id, suggestedNumber) {
    const blob = await this._fetchBlob(`/admin/invoices/${id}/pdf`, 'application/pdf');
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    const filename = `Invoice-${suggestedNumber || id}.pdf`;
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      // Defer revoke so the browser has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    }
    return filename;
  }

  /**
   * Open the thermal-receipt HTML in a new tab. The receipt page
   * auto-fires window.print() on load, so the user just sees the OS
   * print dialog, picks their thermal printer, and hits Print.
   * widthMm: 80 | 58 (default 80, the most common thermal width).
   */
  async printReceiptBrowser(id, { widthMm = 80, autoPrint = true } = {}) {
    const blob = await this._fetchBlob(
      `/admin/invoices/${id}/receipt?width=${widthMm}&autoPrint=${autoPrint ? 1 : 0}`,
      'text/html'
    );
    const htmlBlob = blob.type ? blob : new Blob([blob], { type: 'text/html' });
    const url = URL.createObjectURL(htmlBlob);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return url;
  }

  /**
   * Send ESC/POS commands directly to the configured thermal printer.
   * No print dialog — paper comes out instantly. Requires the printer
   * to be configured in admin Settings → Thermal Printer.
   * Optional overrides: { mode, address, width, type, force }.
   */
  async printReceiptDirect(id, overrides = {}) {
    const data = await this.post(
      `/admin/invoices/${id}/receipt/print`,
      overrides,
      { operation: 'Direct Print Receipt' }
    );
    return data;
  }

  /**
   * Same as downloadInvoicePdf but opens the PDF inline in a new tab
   * instead of downloading it. Useful for "Preview PDF" buttons.
   */
  // ─── REPORTS ──────────────────────────────────────────────────
  // All accept { from, to } (YYYY-MM-DD strings or Date objects).

  async getReportSummary({ from, to, compare = true } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    qs.set('compare', compare ? 'true' : 'false');
    const data = await this.get(`/admin/reports/summary?${qs.toString()}`, { operation: 'Report Summary' });
    return data?.success ? data.data : null;
  }

  async getRevenueSeries({ from, to, granularity = 'day' } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    qs.set('granularity', granularity);
    const data = await this.get(`/admin/reports/revenue-series?${qs.toString()}`, { operation: 'Revenue Series' });
    return data?.success ? data.data : null;
  }

  async getTopItems({ from, to, limit = 10, sortBy = 'revenue' } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    qs.set('limit', String(limit));
    qs.set('sortBy', sortBy);
    const data = await this.get(`/admin/reports/top-items?${qs.toString()}`, { operation: 'Top Items' });
    return data?.success ? data.data : null;
  }

  async getTopCategories({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const data = await this.get(`/admin/reports/top-categories?${qs.toString()}`, { operation: 'Top Categories' });
    return data?.success ? data.data : null;
  }

  async getOrderTypes({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const data = await this.get(`/admin/reports/order-types?${qs.toString()}`, { operation: 'Order Types' });
    return data?.success ? data.data : null;
  }

  async getPaymentMix({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const data = await this.get(`/admin/reports/payment-mix?${qs.toString()}`, { operation: 'Payment Mix' });
    return data?.success ? data.data : null;
  }

  async getHeatmap({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const data = await this.get(`/admin/reports/heatmap?${qs.toString()}`, { operation: 'Heatmap' });
    return data?.success ? data.data : null;
  }

  async getReportCustomers({ from, to, limit = 10 } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    qs.set('limit', String(limit));
    const data = await this.get(`/admin/reports/customers?${qs.toString()}`, { operation: 'Customers Report' });
    return data?.success ? data.data : null;
  }

  async getExpenseBreakdown({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const data = await this.get(`/admin/reports/expense-breakdown?${qs.toString()}`, { operation: 'Expense Breakdown' });
    return data?.success ? data.data : null;
  }

  async getStockSpend({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const data = await this.get(`/admin/reports/stock-spend?${qs.toString()}`, { operation: 'Stock Spend' });
    return data?.success ? data.data : null;
  }

  /**
   * Download the comprehensive PDF report. Returns the filename used.
   */
  async downloadReportPdf({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const blob = await this._fetchBlob(`/admin/reports/pdf?${qs.toString()}`, 'application/pdf');
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    const f = _isoDate(from || new Date());
    const t = _isoDate(to || new Date());
    const filename = `SCR-Report-${f}_to_${t}.pdf`;
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    return filename;
  }

  /**
   * Triggers a CSV download via authenticated fetch + blob URL trick.
   * type: 'summary' | 'items' | 'categories' | 'orders'
   */
  async exportReportCsv({ type = 'summary', from, to } = {}) {
    const qs = new URLSearchParams();
    qs.set('type', type);
    if (from) qs.set('from', _isoDate(from));
    if (to)   qs.set('to',   _isoDate(to));
    const blob = await this._fetchBlob(`/admin/reports/export?${qs.toString()}`, 'text/csv');
    const csvBlob = blob.type === 'text/csv' ? blob : new Blob([blob], { type: 'text/csv' });
    const url = URL.createObjectURL(csvBlob);
    const filename = `${type}_${_isoDate(from || new Date())}_to_${_isoDate(to || new Date())}.csv`;
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.style.display = 'none';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
    return filename;
  }

  async openInvoicePdf(id) {
    const blob = await this._fetchBlob(`/admin/invoices/${id}/pdf?inline=1`, 'application/pdf');
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
    const url = URL.createObjectURL(pdfBlob);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return url;
  }

  // =====================
  // STOCK APIs
  // =====================

  async getStocks(filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) params.append(k, String(v));
      });
      const { page = 1, limit = 100 } = pagination;
      params.append('page', String(page));
      params.append('limit', String(limit));
      const data = await this.get(`/admin/stocks?${params.toString()}`, {
        operation: 'List Stocks', cache: false,
      });
      return data.success ? data : { data: [], total: 0 };
    } catch (err) {
      console.error('Error fetching stocks:', err);
      return { data: [], total: 0 };
    }
  }

  async getStock(id) {
    try {
      const data = await this.get(`/admin/stocks/${id}`, { operation: 'Get Stock', cache: false });
      return data.success ? data.data : null;
    } catch (err) {
      console.error('Error fetching stock:', err);
      return null;
    }
  }

  async createStock(payload) {
    const data = await this.post('/admin/stocks', payload, { operation: 'Create Stock' });
    if (!data.success) throw new Error(data.message || 'Failed to create stock');
    return data.data;
  }

  async updateStock(id, payload) {
    const data = await this.patch(`/admin/stocks/${id}`, payload, { operation: 'Update Stock' });
    if (!data.success) throw new Error(data.message || 'Failed to update stock');
    return data.data;
  }

  async deleteStock(id) {
    const data = await this.delete(`/admin/stocks/${id}`, { operation: 'Delete Stock' });
    if (!data.success) throw new Error(data.message || 'Failed to delete stock');
    return data;
  }

  async recordStockPurchase(id, payload) {
    const data = await this.post(`/admin/stocks/${id}/purchase`, payload, { operation: 'Record Stock Purchase' });
    if (!data.success) throw new Error(data.message || 'Failed to record purchase');
    return data.data;
  }

  // One-shot fix for old stocks that were seeded with qty + cost via "Add
  // Stock" before the create endpoint started recording an initial purchase.
  // Idempotent — safe to call multiple times.
  async backfillStockInitialPurchases() {
    const data = await this.post('/admin/stocks/backfill-initial-purchases', {}, { operation: 'Backfill Stock Initial Purchases' });
    if (!data.success) throw new Error(data.message || 'Backfill failed');
    return data.data;
  }

  // ─── STAFF MANAGEMENT ─────────────────────────────────────────────
  async listStaff() {
    const data = await this.get('/admin/staff', { operation: 'List Staff', cache: false });
    if (!data.success) throw new Error(data.message || 'Failed to load staff');
    return data.data;
  }

  async createStaff(payload) {
    const data = await this.post('/admin/staff', payload, { operation: 'Create Staff' });
    if (!data.success) throw new Error(data.message || 'Failed to create staff');
    return data.data;
  }

  async updateStaff(id, payload) {
    const data = await this.patch(`/admin/staff/${id}`, payload, { operation: 'Update Staff' });
    if (!data.success) throw new Error(data.message || 'Failed to update staff');
    return data.data;
  }

  async deleteStaff(id) {
    const data = await this.delete(`/admin/staff/${id}`, { operation: 'Delete Staff' });
    if (!data.success) throw new Error(data.message || 'Failed to delete staff');
    return data.data;
  }

  async getMyPermissions() {
    const data = await this.get('/admin/me/permissions', { operation: 'My Permissions', cache: false });
    if (!data.success) throw new Error(data.message || 'Failed to load permissions');
    return data.data;
  }

  // ─── AUDIT LOG (read-only) ────────────────────────────────────────
  async listAuditLogs(params = {}) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') qs.set(k, v);
    }
    const data = await this.get(`/admin/audit-logs?${qs.toString()}`, { operation: 'List Audit Logs', cache: false });
    if (!data.success) throw new Error(data.message || 'Failed to load audit log');
    return data.data;
  }

  async getAuditFacets() {
    const data = await this.get('/admin/audit-logs/facets', { operation: 'Audit Facets', cache: false });
    if (!data.success) throw new Error(data.message || 'Failed to load audit facets');
    return data.data;
  }

  async adjustStock(id, delta, reason, type = 'manual-adjust') {
    const data = await this.post(`/admin/stocks/${id}/adjust`, { delta, reason, type }, { operation: 'Adjust Stock' });
    if (!data.success) throw new Error(data.message || 'Failed to adjust stock');
    return data.data;
  }

  // =====================
  // EXPENSE APIs
  // =====================

  async getExpenses(filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) params.append(k, String(v));
      });
      const { page = 1, limit = 100 } = pagination;
      params.append('page', String(page));
      params.append('limit', String(limit));
      const data = await this.get(`/admin/expenses?${params.toString()}`, {
        operation: 'List Expenses', cache: false,
      });
      return data.success ? data : { data: [], total: 0, sumOfFiltered: 0 };
    } catch (err) {
      console.error('Error fetching expenses:', err);
      return { data: [], total: 0, sumOfFiltered: 0 };
    }
  }

  async createExpense(payload) {
    const data = await this.post('/admin/expenses', payload, { operation: 'Create Expense' });
    if (!data.success) throw new Error(data.message || 'Failed to create expense');
    return data.data;
  }

  async updateExpense(id, payload) {
    const data = await this.patch(`/admin/expenses/${id}`, payload, { operation: 'Update Expense' });
    if (!data.success) throw new Error(data.message || 'Failed to update expense');
    return data.data;
  }

  async deleteExpense(id) {
    const data = await this.delete(`/admin/expenses/${id}`, { operation: 'Delete Expense' });
    if (!data.success) throw new Error(data.message || 'Failed to delete expense');
    return data;
  }

  async updateOrderStatus(id, status, notes = '') {
    try {
      const data = await this.patch(`/orders/${id}/status`, { status, notes }, {
        operation: 'Update Order Status',
      });
      this.clearCache('/orders');
      return data;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Acknowledge / resolve a customer's in-flight request (note, call-waiter,
  // bill, water) from the waiter floor. The backend emits customer:request:ack
  // so the customer's order-tracker panel updates live.
  async ackCustomerRequest(orderId, reqId, status = 'acknowledged') {
    const data = await this.patch(
      `/admin/orders/${orderId}/customerRequests/${reqId}/ack`,
      { status },
      { operation: 'Acknowledge Customer Request' }
    );
    if (!data || !data.success) throw new Error(data?.message || 'Could not update the request');
    return data.data;
  }

  // ─── Order state-machine transitions ─────────────────────────────────
  // These hit the new role-gated /admin/orders/:id/<transition> endpoints
  // backed by services/orderStateMachine.js on the server. Each writes a
  // statusHistory entry, audits, and emits role-targeted sockets.
  async confirmOrder(id, { note } = {}) {
    const data = await this.post(`/admin/orders/${id}/confirm`, { note }, { operation: 'Confirm Order' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Confirm failed');
    return data.data;
  }

  async startPreparingOrder(id, { estimatedMinutes, note, assign } = {}) {
    const data = await this.post(`/admin/orders/${id}/start-preparing`,
      { estimatedMinutes, note, assign }, { operation: 'Start Preparing' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Start preparing failed');
    return data.data;
  }

  async extendOrderEta(id, { minutes, note } = {}) {
    const data = await this.post(`/admin/orders/${id}/extend-eta`, { minutes, note }, { operation: 'Extend Order ETA' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Extend ETA failed');
    return data.data;
  }

  async markOrderReady(id, { note } = {}) {
    const data = await this.post(`/admin/orders/${id}/mark-ready`, { note }, { operation: 'Mark Ready' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Mark ready failed');
    return data.data;
  }

  async markOrderServed(id, { note } = {}) {
    const data = await this.post(`/admin/orders/${id}/mark-served`, { note }, { operation: 'Mark Served' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Mark served failed');
    return data.data;
  }

  async markOrderOutForDelivery(id, { note } = {}) {
    const data = await this.post(`/admin/orders/${id}/out-for-delivery`, { note }, { operation: 'Out for Delivery' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Mark out for delivery failed');
    return data.data;
  }

  async completeOrder(id, { note } = {}) {
    const data = await this.post(`/admin/orders/${id}/complete`, { note }, { operation: 'Complete Order' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Complete failed');
    return data.data;
  }

  async addItemsToOrder(id, items = []) {
    const data = await this.post(`/admin/orders/${id}/items`, { items }, { operation: 'Add Items' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Add items failed');
    return data.data;
  }

  // Active orders the waiter cares about. We fetch all order types
  // here and let the floor view exclude takeaway/delivery client-side.
  // Filtering server-side via ?orderType=dine-in turned out to be too
  // strict: legacy rows with a missing or oddly-cased orderType (and
  // any data inserted outside the schema validator) would silently
  // disappear from the Ready-for-pickup column. Excluding
  // takeaway/delivery client-side gets us the same UX without
  // dropping unknowns. Cancelled orders are fetched separately via
  // getRecentlyCancelledOrders.
  async getFloorOrders() {
    const statuses = ['pending', 'confirmed', 'preparing', 'ready'];
    const results = await Promise.all(statuses.map((s) =>
      this.get(`/admin/orders?status=${s}&limit=100&sortBy=createdAt&sortOrder=asc`,
               { operation: `Floor: ${s}`, cache: false })
    ));
    const items = [];
    for (const r of results) {
      if (r && r.success && Array.isArray(r.data)) items.push(...r.data);
    }
    return items;
  }

  // Manually force a table back to Available. Used by the floor view's
  // Free-table override for walkouts and out-of-sync state.
  async releaseTable(tableId, { reason } = {}) {
    const data = await this.post(`/admin/tables/${tableId}/release`,
      reason ? { reason } : {},
      { operation: 'Release Table' });
    if (!data || !data.success) throw new Error(data?.message || 'Release failed');
    return data.data;
  }

  // Last hour's served orders for the "Recently served" reference section.
  async getRecentlyServedOrders({ hours = 1, limit = 30 } = {}) {
    const from = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    const r = await this.get(
      `/admin/orders?status=served&from=${encodeURIComponent(from)}&limit=${limit}&sortBy=updatedAt&sortOrder=desc`,
      { operation: 'Recently Served', cache: false }
    );
    return (r && r.success && Array.isArray(r.data)) ? r.data : [];
  }

  // Available menu items, used by the waiter's add-items modal. We use
  // the public /menu endpoint here because chef/waiter both have menu:read.
  async getAvailableMenu() {
    const r = await this.get('/menu', { operation: 'Public Menu', cache: true });
    if (!r) return [];
    const list = Array.isArray(r.data) ? r.data : (Array.isArray(r) ? r : []);
    return list.filter((m) => m && m.isAvailable !== false);
  }

  async cancelOrder(id, { reason } = {}) {
    const data = await this.post(`/admin/orders/${id}/cancel`, { reason }, { operation: 'Cancel Order' });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Cancel failed');
    return data.data;
  }

  // Cancelled orders from the last N hours (default 2). Powers the
  // "Recently cancelled" section in the Kitchen Display so the chef can
  // see who cancelled what and why.
  async getRecentlyCancelledOrders({ hours = 2, limit = 50 } = {}) {
    const from = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    const r = await this.get(
      `/admin/orders?status=cancelled&from=${encodeURIComponent(from)}&limit=${limit}&sortBy=updatedAt&sortOrder=desc`,
      { operation: 'Recently Cancelled', cache: false }
    );
    return (r && r.success && Array.isArray(r.data)) ? r.data : [];
  }

  // Kitchen-focused view — only active states the chef cares about.
  // Backend returns the same /admin/orders payload, we just preload
  // the right statuses + a big page limit since the board is small.
  async getKitchenOrders() {
    const statuses = ['pending', 'confirmed', 'preparing', 'ready'];
    // /admin/orders only accepts a single status, so issue four cheap calls
    // in parallel and merge. The result list is tiny in practice (active
    // queue is rarely > 30 orders) so the cost is negligible.
    const results = await Promise.all(statuses.map((s) =>
      this.get(`/admin/orders?status=${s}&limit=100&sortBy=createdAt&sortOrder=asc`,
               { operation: `Kitchen: ${s}`, cache: false })
    ));
    const items = [];
    for (const r of results) {
      if (r && r.success && Array.isArray(r.data)) items.push(...r.data);
    }
    return items;
  }

  /**
   * Full edit of an order — items, special requests, customer info, etc.
   * Backend recomputes pricing from DB prices + Settings. Cancelled orders
   * cannot be edited.
   */
  async updateOrder(id, payload) {
    const data = await this.patch(`/admin/orders/${id}`, payload, {
      operation: 'Update Order',
    });
    this.clearCache('/orders');
    if (!data.success) throw new Error(data.message || 'Failed to update order');
    return data.data;
  }

  async getOrderDetails(id) {
    try {
      const data = await this.get(`/orders/${id}`, {
        operation: 'Get Order Details',
        cache: true,
      });
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching order details:', error);
      return null;
    }
  }

  async assignOrderToStaff(orderId, staffId) {
    try {
      const data = await this.patch(`/orders/${orderId}/assign`, { staffId }, {
        operation: 'Assign Order to Staff',
      });
      this.clearCache('/orders');
      return data;
    } catch (error) {
      console.error('Error assigning order to staff:', error);
      throw error;
    }
  }

  async updateOrderItems(orderId, items) {
    try {
      const data = await this.patch(`/orders/${orderId}/items`, { items }, {
        operation: 'Update Order Items',
      });
      this.clearCache('/orders');
      return data;
    } catch (error) {
      console.error('Error updating order items:', error);
      throw error;
    }
  }

  async refundOrder(orderId, amount, reason) {
    try {
      const data = await this.post(`/orders/${orderId}/refund`, { amount, reason }, {
        operation: 'Refund Order',
      });
      this.clearCache('/orders');
      return data;
    } catch (error) {
      console.error('Error refunding order:', error);
      throw error;
    }
  }

  // =====================
  // RESERVATION MANAGEMENT APIs
  // =====================

  // Admin endpoints for reservations (separate from customer side which
  // filters by req.user._id). Hits /admin/reservations.
  async getAdminReservations(filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) params.append(k, String(v));
      });
      const { page = 1, limit = 100, sortBy = 'date', sortOrder = 'desc' } = pagination;
      params.append('page', String(page));
      params.append('limit', String(limit));
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      const url = `/admin/reservations${params.toString() ? '?' + params.toString() : ''}`;
      const data = await this.get(url, { operation: 'List Reservations (Admin)', cache: false });
      return data?.success
        ? data
        : { data: [], total: 0, currentPage: 1, totalPages: 1 };
    } catch (error) {
      console.error('Error fetching admin reservations:', error);
      return { data: [], total: 0, currentPage: 1, totalPages: 1 };
    }
  }

  async updateReservationStatus(id, status, notes = '') {
    try {
      const data = await this.patch(`/admin/reservations/${id}/status`, { status, notes }, {
        operation: 'Update Reservation Status (Admin)',
      });
      this.clearCache('/admin/reservations');
      return data;
    } catch (error) {
      console.error('Error updating reservation status:', error);
      throw error;
    }
  }

  async updateAdminReservation(id, updates) {
    try {
      const data = await this.patch(`/admin/reservations/${id}`, updates, {
        operation: 'Update Reservation (Admin)',
      });
      this.clearCache('/admin/reservations');
      return data;
    } catch (error) {
      console.error('Error updating reservation:', error);
      throw error;
    }
  }

  async getAllReservations(filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      // Add pagination
      const { page = 1, limit = 20, sortBy = 'date', sortOrder = 'asc' } = pagination;
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      const url = `/reservations${params.toString() ? '?' + params.toString() : ''}`;
      const data = await this.get(url, {
        operation: 'Get All Reservations',
        cache: false,
      });
      return data.success ? data : { data: [], total: 0, currentPage: 1, totalPages: 1 };
    } catch (error) {
      console.error('Error fetching reservations:', error);
      return { data: [], total: 0, currentPage: 1, totalPages: 1 };
    }
  }

  async updateReservationStatusLegacy(id, status, notes = '') {
    try {
      const data = await this.patch(`/reservations/${id}/status`, { status, notes }, {
        operation: 'Update Reservation Status (Legacy)',
      });
      this.clearCache('/reservations');
      return data;
    } catch (error) {
      console.error('Error updating reservation status:', error);
      throw error;
    }
  }

  async assignReservationTable(reservationId, tableId) {
    try {
      const data = await this.patch(`/reservations/${reservationId}/table`, { tableId }, {
        operation: 'Assign Reservation Table',
      });
      this.clearCache('/reservations');
      return data;
    } catch (error) {
      console.error('Error assigning reservation table:', error);
      throw error;
    }
  }

  async updateReservationDetails(reservationId, updates) {
    try {
      const data = await this.patch(`/reservations/${reservationId}`, updates, {
        operation: 'Update Reservation Details',
      });
      this.clearCache('/reservations');
      return data;
    } catch (error) {
      console.error('Error updating reservation details:', error);
      throw error;
    }
  }

  // =====================
  // STAFF MANAGEMENT APIs
  // =====================

  async getStaffList(includeInactive = false) {
    try {
      const params = includeInactive ? '?includeInactive=true' : '';
      const data = await this.get(`/users/staff${params}`, {
        operation: 'Get Staff List',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching staff list:', error);
      return [];
    }
  }

  async createStaffMember(staffData) {
    try {
      // Use the main auth register endpoint with role
      const data = await this.post('/auth/register', { ...staffData }, {
        operation: 'Create Staff Member',
      });
      this.clearCache('/users/staff');
      return data;
    } catch (error) {
      console.error('Error creating staff member:', error);
      throw error;
    }
  }

  async updateStaffMember(id, staffData) {
    try {
      const data = await this.put(`/users/${id}`, staffData, {
        operation: 'Update Staff Member',
      });
      this.clearCache('/users/staff');
      return data;
    } catch (error) {
      console.error('Error updating staff member:', error);
      throw error;
    }
  }

  async deleteStaffMember(id) {
    try {
      const data = await this.delete(`/users/${id}`, {
        operation: 'Delete Staff Member',
      });
      this.clearCache('/users/staff');
      return data;
    } catch (error) {
      console.error('Error deleting staff member:', error);
      throw error;
    }
  }

  async updateStaffStatus(id, status) {
    try {
      const data = await this.patch(`/users/${id}/status`, { status }, {
        operation: 'Update Staff Status',
      });
      this.clearCache('/users/staff');
      return data;
    } catch (error) {
      console.error('Error updating staff status:', error);
      throw error;
    }
  }

  async getStaffPerformance(staffId, timeRange = '30d') {
    try {
      const data = await this.get(`/users/${staffId}/performance?range=${timeRange}`, {
        operation: 'Get Staff Performance',
        cache: true,
      });
      return data.success ? data.data : {};
    } catch (error) {
      console.error('Error fetching staff performance:', error);
      return {};
    }
  }

  // =====================
  // CUSTOMER MANAGEMENT APIs
  // =====================

  async getCustomers(filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const { page = 1, limit = 20, sortBy = 'createdAt', sortOrder = 'desc' } = pagination;
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      const url = `/users/customers${params.toString() ? '?' + params.toString() : ''}`;
      const data = await this.get(url, {
        operation: 'Get Customers',
        cache: true,
      });
      return data.success ? data : { data: [], total: 0, currentPage: 1, totalPages: 1 };
    } catch (error) {
      console.error('Error fetching customers:', error);
      return { data: [], total: 0, currentPage: 1, totalPages: 1 };
    }
  }

  async getCustomerDetails(customerId) {
    try {
      const data = await this.get(`/users/${customerId}`, {
        operation: 'Get Customer Details',
        cache: true,
      });
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      return null;
    }
  }

  async updateCustomerStatus(customerId, status, reason = '') {
    try {
      const data = await this.patch(`/users/${customerId}/status`, { status, reason }, {
        operation: 'Update Customer Status',
      });
      this.clearCache('/users/customers');
      return data;
    } catch (error) {
      console.error('Error updating customer status:', error);
      throw error;
    }
  }

  // =====================
  // ANALYTICS APIs
  // =====================

  async getRevenueAnalytics(period = '7d') {
    try {
      const data = await this.get(`/analytics/revenue?period=${period}`, {
        operation: 'Get Revenue Analytics',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching revenue analytics:', error);
      return [];
    }
  }

  async getPopularItems(limit = 10, timeRange = '30d') {
    try {
      const data = await this.get(`/analytics/popular-items?limit=${limit}&range=${timeRange}`, {
        operation: 'Get Popular Items',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching popular items:', error);
      return [];
    }
  }

  async getTableUtilization(timeRange = '7d') {
    try {
      const data = await this.get(`/analytics/table-utilization?range=${timeRange}`, {
        operation: 'Get Table Utilization',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching table utilization:', error);
      return [];
    }
  }

  async getCustomerAnalytics(timeRange = '30d') {
    try {
      const data = await this.get(`/analytics/customers?range=${timeRange}`, {
        operation: 'Get Customer Analytics',
        cache: true,
      });
      return data.success ? data.data : {};
    } catch (error) {
      console.error('Error fetching customer analytics:', error);
      return {};
    }
  }

  async getOrderTrends(timeRange = '30d', granularity = 'day') {
    try {
      const data = await this.get(`/analytics/order-trends?range=${timeRange}&granularity=${granularity}`, {
        operation: 'Get Order Trends',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching order trends:', error);
      return [];
    }
  }

  // =====================
  // SETTINGS APIs
  // =====================

  async getSettings() {
    try {
      const data = await this.get('/settings', {
        operation: 'Get Settings',
        cache: true,
      });
      return data.success ? data.data : {};
    } catch (error) {
      console.error('Error fetching settings:', error);
      return {};
    }
  }

  async updateSettings(settings) {
    try {
      const data = await this.put('/settings', settings, {
        operation: 'Update Settings',
      });
      this.clearCache('/settings');
      return data;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  async updateSettingSection(section, settings) {
    try {
      const data = await this.patch(`/settings/${section}`, settings, {
        operation: 'Update Setting Section',
      });
      this.clearCache('/settings');
      return data;
    } catch (error) {
      console.error('Error updating setting section:', error);
      throw error;
    }
  }

  // =====================
  // BACKUP & EXPORT APIs
  // =====================

  async exportData(type, format = 'csv', filters = {}) {
    try {
      const params = new URLSearchParams({ format });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await fetch(`${this.baseURL}/export/${type}?${params.toString()}`, {
        headers: AdminRequestInterceptor.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new AdminApiError(`Export failed: ${response.statusText}`, response.status);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  async createBackup() {
    try {
      const data = await this.post('/backup/create', null, {
        operation: 'Create Backup',
        timeout: 120000, // 2 minutes for backup
      });
      return data;
    } catch (error) {
      console.error('Error creating backup:', error);
      throw error;
    }
  }

  async getBackupHistory() {
    try {
      const data = await this.get('/backup/history', {
        operation: 'Get Backup History',
        cache: true,
      });
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching backup history:', error);
      return [];
    }
  }

  async downloadBackup(backupId) {
    try {
      const response = await fetch(`${this.baseURL}/backup/${backupId}/download`, {
        headers: AdminRequestInterceptor.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new AdminApiError(`Backup download failed: ${response.statusText}`, response.status);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error downloading backup:', error);
      throw error;
    }
  }

  // =====================
  // REAL-TIME FEATURES (WebSocket)
  // =====================

  connectWebSocket() {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:5000'}/admin?token=${token}`;
      this.wsConnection = new WebSocket(wsUrl);

      this.wsConnection.onopen = () => {
        console.log('Admin WebSocket connected');
        this.emit('ws:connected');
      };

      this.wsConnection.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.emit(`ws:${data.type}`, data.payload);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.wsConnection.onclose = () => {
        console.log('Admin WebSocket disconnected');
        this.emit('ws:disconnected');
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (localStorage.getItem('authToken')) {
            this.connectWebSocket();
          }
        }, 5000);
      };

      this.wsConnection.onerror = (error) => {
        console.error('Admin WebSocket error:', error);
        this.emit('ws:error', error);
      };
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
    }
  }

  disconnectWebSocket() {
    if (this.wsConnection) {
      this.wsConnection.close();
      this.wsConnection = null;
    }
  }

  sendWebSocketMessage(type, payload) {
    if (this.wsConnection && this.wsConnection.readyState === WebSocket.OPEN) {
      this.wsConnection.send(JSON.stringify({ type, payload }));
    }
  }

  // Event system for WebSocket and other events
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }

  // =======================
  // REPORTING APIs
  // =======================
  
    // Settings management
  async getSettings(category) {
    const url = category ? `/admin/settings?category=${category}` : '/admin/settings';
    return this.request(url);
  }

  async updateSetting(id, value) {
    // request() handles JSON.stringify itself — don't pre-stringify here,
    // or body-parser receives a string-of-JSON instead of JSON.
    return this.request(`/admin/settings/${id}`, {
      method: 'PUT',
      body: { value },
    });
  }

  async bulkUpdateSettings(updates) {
    return this.request('/admin/settings/bulk', {
      method: 'PATCH',
      body: { updates },
    });
  }

  async resetSettings() {
    return this.request('/admin/settings/reset', { method: 'POST' });
  }


  // =====================
  // UTILITY METHODS
  // =====================

  async getCurrentAdmin() {
    try {
      const data = await this.get('/auth/profile', {
        operation: 'Get Current Admin',
        cache: true,
      });
      return data.success ? data.user : null;
    } catch (error) {
      console.error('Error fetching current admin:', error);
      return null;
    }
  }

  async updateAdminProfile(updates) {
    try {
      const data = await this.put('/auth/profile', updates, {
        operation: 'Update Admin Profile',
      });
      this.clearCache('/auth/profile');
      return data;
    } catch (error) {
      console.error('Error updating admin profile:', error);
      throw error;
    }
  }

  getAuditLog() {
    return AdminRequestManager.getAuditLog();
  }

  clearAuditLog() {
    AdminRequestManager.clearAuditLog();
  }

  // Utility method to download any blob as file
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // Admin-specific formatting methods
  formatAdminDate(date, options = {}) {
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    
    return new Date(date).toLocaleDateString('en-US', { ...defaultOptions, ...options });
  }

  // Health check for admin services
  async adminHealthCheck() {
    try {
      const data = await this.get('/health', { 
        timeout: 5000,
        operation: 'Health Check',
      });
      return data;
    } catch (error) {
      console.error('Admin health check failed:', error);
      return { success: false, error: error.message };
    }
  }
}

// =============================================
// CREATE AND EXPORT SINGLETON INSTANCES
// =============================================

// Create singleton instances
const apiService = new ApiService();
const adminApiService = new AdminApiService();

// Export error classes for use in components
export { 
  ApiError, 
  NetworkError, 
  ValidationError,
  AdminApiError,
  AdminNetworkError 
};

export { adminApiService as AdminApiService };  // named export
export default apiService;  // default export